const BUILDING_INSPECTION = {
  // help text shown in the info "i" next to inspection fields
  tooltips: {
    info: {
      buildingCategory:
        "Select the building's main category, for example office, school or warehouse. The field is used to choose the correct standard values and profiles in the calculation.",
      yearOfConstruction:
        "Enter the building's construction year, or the year of the most recent major renovation if that is more representative. Used among other things to assess the likely technical standard.",
      numberOfFloors:
        'Enter the total number of floors above ground for the building. The field is used as a basis for geometry, volume and further division into zones.',
    },
    thermalZones: {
      zoneCategory:
        'A thermal zone is a part of the building treated as one unit in the calculation, typically with similar use and indoor climate. Give the zone a clear name and select a zone category. The category sets the standard profiles for operating hours, internal loads and technical assumptions.',
      buildingSubcategory:
        'Select a subcategory if you want a more precise classification of the zone. Leave the field empty if the regular zone category is sufficient.',
      zoneClimate:
        'Indicates whether the zone is heated, partly heated or unheated. The climate status determines how the zone enters the heating and cooling demand and is a fundamental precondition for the energy calculation.',
      bra: 'BRA is the usable floor area of the zone, i.e. the floor area within the external walls counted as heated area. Enter BRA in m². The area is one of the most important inputs and directly affects almost every calculation.',
      floors:
        'Enter how many floors the zone spans if it is not limited to a single level. If the zone is on a single level, the field can often be left empty.',
      floorHeight:
        'Enter the average floor height in metres for the zone. Used together with the area to calculate volume and air flow rates.',
      indoorTemp:
        'The temperature the zone has when the simulation starts. It is not a setpoint: the heating and cooling setpoints are set in the Heating Schedule and Cooling Schedule.',
      tekStandard:
        'TEK refers to the Norwegian building regulations, for example TEK10 and TEK17, which set requirements for U-values, airtightness and more. Select the relevant TEK standard if you want to control the standard values more precisely than the construction year alone allows.',
      airLeakageN50:
        'The air leakage number (n50) describes how airtight the building is, measured as air changes per hour at 50 Pa pressure difference. Enter the value if it is known from a pressurisation test or documentation. If the field is empty, representative standard values are used.',
      thermalBridgeCoeff:
        'A thermal bridge is a place in the construction with increased heat loss, typically where floors and walls meet the external structure. Select the structural system or thermal bridge category if you want to model the thermal bridges more precisely. Normally used only when you have good building physics information.',
      thermalBridgePsiValue:
        'The normalized thermal bridge value (ψ) expresses the total extra heat loss from thermal bridges per m² of floor area. Enter the value if it is known from calculation. Can normally be left empty in a standard survey.',
      heatingEmissionSystem:
        'The heat emission system is how heat is delivered to the room, for example radiator, convector or underfloor heating. The choice affects the required supply temperature and control. Useful for detailing, but usually not necessary in the initial registration.',
      coolingEmissionSystem:
        'The cooling emission system is how cooling is delivered to the room, for example chilled beam, fan coil or chilled ceiling. The choice affects the temperature level and control. Useful for detailing, but usually not necessary in the initial registration.',
      zoneConnections:
        'A zone connection describes a surface where two zones, or a zone and a climate condition, exchange heat, for example an internal wall facing a colder zone. Use this when such heat exchange matters for the model. Normally only relevant in a more detailed setup.',
      connectionArea:
        'Enter the area in m² of the surface connecting the zones. Together with the U-value it determines how much heat is exchanged. Only filled in when you are actually modelling a zone connection.',
      connectionUValue:
        'The U-value describes how well the surface between the zones insulates (W/m²K). Enter it if you have it available. If not, the connection can usually be simplified or omitted.',
      heatingSchedule:
        'A heating schedule describes how the setpoint and operation vary over the day and week, for example night setback. Use it when the zone has deviating operation. Gives a more realistic simulation than fixed standard profiles.',
      hasFloorToGround:
        'Enable this if the zone has a floor against the ground. Disabling it hides the floor to ground element and removes its stored area from the energy calculation.',
      hasRoof:
        'Enable this if the zone has a roof facing the outdoor air. Disabling it hides the roof element and removes its stored area from the energy calculation.',
      isBasement:
        'Enable this when the zone is a basement below ground. The heat loss to the ground is then calculated from the Basement construction element, and roof, floor to ground, exterior walls, windows and doors on this zone are left out of the energy calculation.',
      basement: {
        area: 'Enter the floor area of the basement against the ground in m². Required for the ground heat loss calculation and normally equal to the BRA of the basement zone.',
        wallPerimeter:
          'Enter the length in metres of basement wall that faces the ground. Together with the area it gives the characteristic dimension of the floor. If left empty, a square footprint is assumed.',
        fillHeight:
          'Enter the average depth from the terrain down to the basement floor in metres. Determines how much of the wall loses heat to the ground. 0 means a slab on ground.',
        wallThickness:
          'Enter the full thickness of the basement wall at ground level in metres, including insulation. 0.3 m is used when left empty.',
        wallConstructionType:
          'Select the construction of the basement wall below ground, or a TEK period to use its requirement. The choice suggests the U-value.',
        wallUValue:
          'The U-value of the basement wall construction below ground (W/m²K). Enter it if you know it from documentation, otherwise the suggested value is used.',
        floorConstructionType:
          'Select the construction of the basement floor against the ground, or a TEK period to use its requirement. The choice suggests the U-value.',
        floorUValue:
          'The U-value of the basement floor construction against the ground (W/m²K). Enter it if you know it from documentation, otherwise the suggested value is used.',
      },
      floor: {
        type: 'The floor to ground type describes how the floor against the ground or an unheated space is built up, for example slab on ground or floor over a basement. Select the one that best describes the construction. The choice is used to suggest relevant standard values, including the U-value.',
        area: 'Enter the floor to ground area in m² for the part of the zone to be registered. The area is required to calculate heat loss through the floor to ground.',
        uValue:
          'The U-value indicates how much heat passes through the construction per m² and degree of temperature difference (W/m²K). A lower value means better insulation. Enter it if you know it from documentation or calculation, otherwise nominal values based on the selected type and year are used.',
      },
      structuralNodes: {
        boundary:
          'A structural node is a place where building elements meet, for example between a floor slab and an external wall, and is typically a thermal bridge with extra heat loss. Use structural nodes when you want to register such details. Normally only relevant at a higher level of detail.',
        length:
          'Enter the length of the relevant node or thermal bridge in metres. Only filled in if structural nodes are used.',
        height:
          'Enter the height of the relevant node in metres if applicable. Used together with the other measurements for a detailed geometry description.',
        width:
          'Enter the width of the relevant node in metres if applicable. The field is only used for detailed registration of structural nodes.',
      },
      roof: {
        angle:
          'The roof angle is the slope of the roof surface in degrees, where 0 means a flat roof. It is recorded for the inspection, but the energy calculation does not use it yet: heat loss through the roof comes from the area and U-value, and roof windows get no solar gain in the calculation.',
        type: 'The roof construction describes how the roof is built up, for example a compact roof, a ventilated pitched roof or trusses. Select the one that best describes the roof. The choice forms the basis for the standard U-value and other assumptions.',
        area: 'Enter the roof area in m² included in the zone. The area is required to calculate transmission through the roof.',
        uValue:
          'The U-value indicates how much heat passes through the roof construction per m² and degree of temperature difference (W/m²K). A lower value means better insulation. Enter it if it is known, otherwise a nominal value based on the selected construction and age is used.',
        glassType:
          'Enable this if the roof has windows or skylights, and select a glass type. The glass type, for example double or triple glazing, determines both the heat loss and how much solar energy enters through the roof surface.',
        windowArea:
          'Enter the total window or skylight area in m². The field is only relevant if the roof actually has windows or skylights.',
        windowUValue:
          'The U-value for the roof windows indicates the heat loss through the glass and frame itself (W/m²K). A lower value means better insulation. Enter it if it is known, otherwise standard values are often sufficient.',
        gValue:
          'The g-value (solar factor) describes the proportion of solar energy that passes through the glass, from 0 to 1. A high g-value gives more free solar heat in winter, but also a greater cooling demand in summer.',
        externalBlindsGt:
          'External blinds are mounted on the outside of the glass and reduce solar radiation effectively. The solar factor (gt) describes how much solar energy passes through when the shading is active. Register this when such solar shading has a clear effect.',
        externalScreenGt:
          'An external screen is an external fabric shading that dampens solar radiation. The solar factor (gt) indicates how much solar energy passes through with the screen lowered. Only fill this in if the solution exists in the building.',
        internalShadingGt:
          'Internal shading, for example blinds or curtains on the inside, dampens the sun but less effectively than external shading. The solar factor (gt) describes the remaining solar gain. Used to improve the calculation of solar gains.',
      },
      groundConditions: {
        type: 'Ground conditions describe the masses beneath the building, for example clay, sand or rock, which affect the heat loss to the ground. Use this when you have specific information about the ground. This is typically a detail field.',
        thermalConductivity:
          'Thermal conductivity (λ) indicates how well the ground conducts heat, in W/mK. A higher value gives a greater heat loss to the ground. Enter a documented value if you have one, otherwise the field can be left empty.',
        heatCapacity:
          'The heat capacity describes how much heat the ground can store, dampening fluctuations in the heat loss over time. Enter it if it is known. Can normally be left empty in an ordinary energy survey.',
        penetrationDepth:
          'The periodic penetration depth is the depth in the ground that is affected by seasonal variations in temperature. It is used in more detailed ground models. The field is usually not necessary in a standard registration.',
      },
      internalLoads: {
        lighting:
          'Lighting is an internal heat gain: the energy the light sources use largely ends up as heat in the zone. Register the lighting level and any operating profiles. This affects both electricity use and cooling demand.',
        equipment:
          'Technical equipment such as computers, monitors and other fixed equipment gives off heat to the room in addition to using electricity, and is therefore an internal heat gain. Register the typical power and operating profile for the zone.',
        occupancy:
          'People give off heat and moisture and at the same time affect the ventilation demand. Register the occupancy through numbers and profiles or a standard setup that describes normal use of the zone.',
        dishwasher:
          'Tick dishwasher if it is a relevant load in the zone. The hot water share indicates how much of the consumption is covered by heated tap water rather than the appliance heating it itself. Normally only used when appliances are significant.',
        washingMachine:
          'Tick washing machine if it is a relevant load in the zone. The hot water share indicates how much of the water the machine draws from the hot water system instead of heating it itself.',
        tumbleDryer:
          'Tick tumble dryer if it is a relevant load in the zone. Any hot water share describes how much of the energy demand is covered via hot tap water rather than direct electricity.',
      },
      thermalMass: {
        heatCapacityCategory:
          "Thermal mass is the building's ability to store heat, which dampens temperature swings and shifts the heating and cooling demand in time. Select a heat capacity category, for example light or heavy construction, if you want to adjust this beyond standard values.",
        normalizedHeatCapacity:
          'The normalized heat capacity expresses how much heat the zone can store per m² of floor area (Wh/m²K). Enter a more precise value if you have one, otherwise the field can be left empty and the category is used.',
        cladding:
          'Internal cladding, for example plasterboard, wood or exposed concrete, affects how much of the thermal mass is actually active toward the room. Select the type if you want a more detailed description.',
        heatCapacity:
          'Enter the heat capacity of the internal walls if it is known. This describes how much heat the internal constructions can store, and is only used for more detailed modelling of thermal mass.',
        furnitureGrade:
          'Furniture and fittings also contribute to thermal mass. Select a furniture grade describing how furnished the zone is if this matters. The field is normally a detail field.',
        massFactor:
          "The mass factor adjusts how much furniture and fittings contribute to the zone's thermal mass. Enter a value if you want to override the standard value.",
      },
      exteriorWall: {
        direction:
          'The orientation determines how much sun the facade receives and how wind and the outdoor climate affect the heat loss. Select the direction, for example north, south, east or west, for the wall being registered.',
        type: 'The wall type describes how the external wall is built up, for example timber frame, masonry or concrete with insulation. Select the one that best describes the wall. The choice is used to suggest relevant standard values.',
        area: 'Enter the gross area of the exterior wall in m², including windows, doors and gates. The calculation subtracts the openings registered on the wall itself, so entering the net area would count too little heat loss through the wall.',
        uValue:
          'The U-value indicates how much heat passes through the wall per m² and degree of temperature difference (W/m²K). A lower value means better insulation. Enter it if it is known, otherwise nominal values based on the wall type and age are used.',
        windowShadingFactor:
          'The share of direct sunlight the window loses to shading from overhangs, nearby buildings or terrain, from 0 to 1. 0 means no shading and 1 means fully shaded. Only direct sun is reduced, not diffuse radiation. The blinds and screens below are recorded but not used in the calculation.',
      },
      heatingCooling: {
        name: 'Give the equipment a clear name that makes it easy to tell components apart. The name is primarily used for identification in the project.',
        type: 'The type describes what kind of heating or cooling unit this is, for example radiator, underfloor heating, heat pump or chilled beam. The choice controls which fields are relevant and how the system is modelled.',
        energySource:
          'The energy source is what the unit uses to produce heating or cooling, for example electricity, district heating or a heat pump. Select the source if the component is directly connected to a specific source, otherwise the energy supply can be described centrally.',
        co2eEmissionFactor:
          'The CO₂e emission factor indicates how much greenhouse gas emission accompanies the energy the unit uses, given per kWh. Enter a specific value if you want to calculate emissions precisely. It does not affect the energy demand itself.',
        quantity:
          'Enter the number of identical units if several identical units are installed. Can be left empty if the power is given as a total.',
        power:
          "The nominal power is the unit's maximum heating or cooling output in kW. It is used to assess whether the system has the capacity to cover the demand, and how multiple systems are prioritised. Enter the total power if several identical units are combined.",
        systemEfficiency:
          'The system efficiency describes how much of the supplied energy actually becomes useful heating or cooling, including losses in distribution and control. Enter a better figure than the standard if you have one. Often not necessary in the initial registration.',
        priority:
          'The priority controls the order in which the systems are used when several cover the same demand. A lower priority means the system is used first, and the rest cover peak load. Set this if the zone has several heating or cooling sources.',
        cop: "COP (Coefficient of Performance) is the heat pump's efficiency for heating: the ratio of delivered heat to electricity supplied. A COP of 3 means three times as much heat as electricity. Enter a specific value if you have one.",
        eer: 'EER (Energy Efficiency Ratio) is the efficiency for cooling: the ratio of delivered cooling to electricity supplied. A higher value means more efficient cooling. Enter a specific value if you have one.',
        nominalLoad:
          'The nominal load (0-1) describes the share of full capacity the unit is designed to run at normally. This is a detail field and can usually be left at the standard value.',
        minimumLoad:
          'The minimum load (0-1) is the lowest share of full capacity the unit can run stably at before it has to cycle on and off. Enter a value if you want to override the standard.',
        degradeCoeff:
          'The degradation coefficient (0-1) describes how much the efficiency drops when the unit runs at part load instead of full power. Inverter-controlled units usually have lower losses. Often set automatically.',
        maxTemperatureSupplied:
          'The maximum supply temperature is the highest temperature the unit can deliver to the heat emission system. It determines whether the unit can cover the temperature demand, for example for radiators that require a high supply temperature.',
      },
      domesticHotWater: {
        name: 'Give the domestic hot water component a clear name if you register several units or subsystems.',
        heaterType:
          'The heater type describes how the domestic hot water is heated, for example a direct electric heater, a heat pump or central supply such as district heating. The choice affects both the energy demand and emissions for hot water.',
        tapFrequency:
          'The tapping frequency describes how often and how much hot water is drawn, and reflects the actual usage pattern in the zone. It is an important driver of the calculated energy demand for hot water.',
        tapCyclesPerDay:
          'The number of tapping cycles per day is how many times hot water is drawn during a day. Enter a value if you want to override the standard profile with a more precise figure.',
        thermalZone:
          'Connect the domestic hot water system to the thermal zone it serves. This places the energy use and any heat emission correctly in the model.',
      },
      productionEquipment: {
        name: 'Give the production equipment a clear name. Used to identify the load in the project and in reports.',
        energySource:
          'The energy source is what the production equipment uses, for example electricity or gas. It is used to distribute energy use to the correct carrier and for any emission calculation.',
        power:
          "The nominal power is the equipment's typical power draw in kW. Together with the operating schedule it provides the basis for calculating energy use and heat emission to the zone.",
        eligibleForHarvesting:
          'Energy harvesting means using surplus heat from the equipment for other purposes, for example heating. Tick this if the equipment emits such surplus energy, so the recovery potential is assessed.',
        operationalSchedule:
          'The operating schedule describes when the equipment is actually in use over the day and week. Add it if the equipment does not follow normal operating hours, for a more realistic calculation of energy use and internal loads.',
      },
      hvac: {
        name: 'Give the ventilation unit a clear name. The name is used for identification if the building has several units.',
        linkedZones:
          'Linked zones are the thermal zones the ventilation unit serves with air. The choice is necessary to distribute air volumes, fan energy and heat recovery correctly between the zones.',
        flowSfpSettings:
          "Here you enter the unit's air volumes and SFP assumptions. SFP (specific fan power) expresses how much electrical power the fans use per air volume, and is core information for the ventilation calculation.",
        motorType:
          'The motor type, for example AC or EC, affects the fan efficiency and how the power changes at part load. Select the type if it is known for a more precise calculation.',
        sfp100:
          "SFP (specific fan power) is the ratio of the fans' power draw to the air volume, given in kW per m³/s. A lower SFP means a more energy-efficient fan. Enter the value at 100% operation; this is the main parameter in the ventilation calculation.",
        sfp80:
          'SFP at 80% operation describes the specific fan power when the unit runs at a reduced air volume. Enter it if you have part-load data, otherwise the field can be left empty.',
        sfp60:
          'SFP at 60% operation describes the specific fan power at a lower air volume. Only used for more detailed part-load modelling.',
        sfp40:
          'SFP at 40% operation. Enter it if part-load data for such a low air volume is available.',
        sfp20:
          'SFP at 20% operation. Enter it if part-load data for such a low air volume is available.',
        fanPowerIn:
          'The supply fan power is the electrical power draw of the supply fan in kW. Enter it if you have it separately. Can normally be left empty when the SFP is registered, since the power is then calculated from it.',
        fanPowerOut:
          'The extract fan power is the electrical power draw of the extract fan in kW. Enter it if you have it separately. Only used for more detailed registration.',
        coverageArea:
          'The coverage area is the floor area the unit serves in m². Enter it if the unit only covers part of the building or zone. Can often be derived from the connected zones.',
        coverageVolume:
          'The coverage volume is the air volume the unit serves in m³. Enter it if it is known. Used as a detail field for ventilation sizing.',
        airVolumeOperating:
          'The supply air volume in operation is the air volume the unit delivers during normal operation, in m³/h. This is a key input for ventilation demand, fan energy and heating and cooling coils.',
        airVolumeCapacity:
          'The supply air capacity is the maximum air volume the unit can deliver, in m³/h. Enter it if it is known. Useful for capacity assessments, but not always necessary.',
        airVolumeNonOperating:
          'The supply air volume outside of operation is the air volume the unit delivers when the building is not in use, for example during reduced night operation. Enter it if the unit runs with a reduced air volume part of the time.',
        airVolumeExtractionOperating:
          "The extract air volume in operation is the air the unit draws out of the zones during normal operation, in m³/h. Together with the supply air volume it describes the unit's actual air balance and affects fan energy and heat recovery.",
        airVolumeExtractionCapacity:
          'The extract air capacity is the maximum air volume the unit can draw out, in m³/h. Enter it if it is known. Useful for capacity assessments, but not always necessary.',
        airVolumeExtractionNonOperating:
          'The extract air volume outside of operation is the air the unit draws out when the building is not in use, for example during night setback or reduced operation. Enter it if the unit runs with a reduced air volume part of the time.',
        supplyTemperature:
          'The supply air temperature is the temperature of the air the unit blows into the zone, in °C. The setpoints in the operational schedule take priority: this value is only used for hours where the schedule has no setpoint. To change the supply air setpoint, change it in the operational schedule.',
        heatRecoveryType:
          'Heat recovery reuses the heat in the extract air to preheat the supply air, reducing the heating demand. Select the type that is installed, for example a rotary wheel, plate exchanger or run-around coil. The choice is important for the calculation of recovered energy.',
        efficiencyHR:
          'The efficiency indicates how large a share of the heat in the extract air the recovery unit actually transfers to the supply air, as a percentage. A higher value means a lower heating demand. Enter a documented value if you have one, otherwise a standard value based on the selected type is used.',
        frostProtection:
          'Frost protection prevents the recovery unit from freezing up at low outdoor temperatures, but can reduce how much heat is recovered during such periods. Tick this if the unit has frost protection that affects operation or heat recovery. Useful for more precise modelling.',
        frostProtectionTemperature:
          'This is the outdoor temperature at which the frost protection activates. Below this temperature the recovery is reduced or controlled to avoid icing. Enter the value if it is known, otherwise a standard value is used.',
        measuredSupplyTemp:
          'The measured supply air temperature is the actual temperature of the air the unit blows in, recorded during a site visit or logging. Enter it if you have field measurements. Used for quality assurance and more precise calibration of the model.',
        measuredExhaustTemp:
          'The measured exhaust air temperature is the actual temperature of the air drawn out of the zones, recorded by field measurement. Enter it if you have such measurements. Together with the measured supply air temperature it is used for quality assurance.',
        dx: {
          name: 'A DX system (direct expansion) is a cooling or heat pump coil where the refrigerant evaporates directly in the air stream, without a water loop in between. Give the unit a clear name for identification if the unit has such an integrated DX unit.',
          type: 'The type indicates what the DX unit can do: cooling only, heating only, or both (heat pump function). The choice controls which effect and efficiency fields are relevant.',
          heatingEffect:
            'The heating effect is the maximum heat the DX unit can deliver to the air, in kW. Enter it if the system delivers heat. Can be left empty if the unit is only used for cooling.',
          coolingEffect:
            'The cooling effect is the maximum cooling the DX unit can deliver to the air, in kW. This is normally the most important capacity value for cooling operation.',
          seasonalCop:
            "Seasonal COP is the DX unit's average efficiency for heating over a season: the ratio of delivered heat to electricity supplied. Enter it if the unit is also used for heating. Use a standard value if precise documentation is missing.",
          seasonalEer:
            "Seasonal EER is the DX unit's average efficiency for cooling over a season: the ratio of delivered cooling to electricity supplied. Enter it if it is known. Used to calculate electricity use for cooling.",
          systemEfficiency:
            'The calculation uses the system efficiency as the efficiency of the DX unit itself, in percent: 300 % means three times as much heating or cooling as electricity. The seasonal COP/EER fields are not used yet, so enter the COP or EER here as a percentage. If left empty, 100 % is used.',
          energySource:
            'The energy source is what the DX unit uses to produce heating or cooling, normally electricity. It is used for energy distribution and any emission calculation.',
        },
        heatingBattery: {
          name: 'A heating coil in ventilation is a heat exchanger in the unit that warms the supply air, typically fed with hot water from a boiler or heat pump, or electric. Give the coil a clear name if several coils exist in the same unit or building.',
          type: 'The battery type indicates how the heat is supplied, for example water-based (hot water through the coil) or electric. The choice affects the energy source and efficiency.',
          power:
            'The power is the maximum heat the coil can add to the supply air, in kW. Used for capacity checking and energy calculation.',
          systemEfficiency:
            'The system efficiency captures losses in distribution and control up to the coil. Enter a value if you have a better figure than the standard, otherwise the field can be left empty.',
          energySource:
            'The energy source is what supplies the heating coil with heat, for example district heating, a boiler, a heat pump or electricity. It is used for energy distribution and any emission calculation.',
        },
        coolingBattery: {
          name: 'A cooling coil in ventilation is a heat exchanger in the unit that cools the supply air, typically fed with chilled water from a chiller or by direct expansion (DX). Give the coil a clear name if several coils exist.',
          type: 'The type indicates how the cooling is supplied, for example water-based (chilled water through the coil) or DX (direct expansion). The choice affects the energy source and efficiency.',
          power:
            'The power is the maximum cooling the coil can add to the supply air, in kW. Used for capacity checking and energy calculation.',
          systemEfficiency:
            'The system efficiency captures additional losses in distribution and control beyond the nominal performance. Enter a value if you want to include such losses, otherwise the field can be left empty.',
          eer: "EER (Energy Efficiency Ratio) is the cooling coil's efficiency: the ratio of delivered cooling to electricity supplied. Enter it if it is known. Used to calculate electricity use for cooling.",
          energySource:
            'The energy source is what supplies the cooling coil with cooling, for example a chiller, district cooling or DX. It is used for energy distribution and any emission calculation.',
        },
        naturalVentilation:
          'Natural ventilation is air exchange driven by wind and temperature differences through windows, vents or shafts, without fans. Enable this if the zone or unit has natural ventilation that should be included in the model.',
        operationalSchedule:
          'An operational schedule describes when the ventilation is running over the day and week. Enter a schedule for natural ventilation or unit operation if the operation deviates from the standard operating hours.',
        operationalScheduleExtra:
          'Use an additional schedule when the same unit or ventilation function has several operating periods with different operation, for example seasonally. Each schedule covers its own period.',
      },
      centralEnergy: {
        name: 'A central energy module is a shared energy plant that supplies several zones or functions, for example a heating plant or a chiller plant. Give it a clear name. The name is only used for identification.',
        mode: 'The mode indicates whether the plant delivers heating, cooling, hot water or combinations of these. This is a fundamental setting that controls how the plant is used in the model.',
        thermalZone:
          'A plant can be linked to a thermal zone so that the ambient temperature around the plant follows that zone, which affects efficiency and losses. Link it to a relevant zone if placement or ambient temperature matters for the calculation, otherwise it can be left standalone.',
        ambientTemperature:
          'The ambient temperature is the temperature of the space where the plant is located, for example a plant room. It is only used when the plant is not linked to a thermal zone, and affects standby losses from the plant and storage tanks. If a zone is selected, the temperature is taken from there instead.',
        sourceName:
          'Give the energy source a clear name so it is easy to recognise if the plant has several energy sources. The name is only used for identification.',
        usage:
          'These options control what the energy source actually covers: heating, cooling and/or domestic hot water (DHW). Select the demands the source contributes to. Inverter controlled means the unit can modulate its output, which gives better efficiency at part load.',
        heatPump: {
          type: 'The heat pump type describes where the heat pump takes energy from and delivers it to: air-to-water takes energy from outdoor air and delivers to a water-based system, liquid-to-water draws from ground, rock or water via a collector, and air-to-air heats the indoor air directly. The choice affects efficiency and which additional fields are shown.',
          co2eEmissionFactor:
            'The CO₂e emission factor indicates how much greenhouse gas emission accompanies the energy the heat pump uses, given per kWh. Enter a specific value only if you want to calculate emissions precisely. It does not affect the energy demand itself.',
          heatingCapacity:
            "The heating capacity is the heat pump's nominal heat output in kW, i.e. how much heat it can deliver under normal conditions. This is the most important capacity value and is used to assess whether the pump covers the heating demand.",
          nominalLoad:
            'The nominal load (0-1) is the share of full capacity the heat pump is designed to run at normally. Enter it if the documentation states it separately. Used for detailed part-load modelling.',
          minimumLoad:
            'The minimum load (0-1) is the lowest share of full capacity the heat pump can run stably at before it has to cycle on and off. Enter it if the pump can modulate and this is known.',
          degradeCoeff:
            'The degradation coefficient (0-1) describes how much the efficiency drops when the heat pump runs at part load instead of full power. Enter it if manufacturer data or calculations are available. Can normally be left empty; it is then set to 0.8 per NS 3031, or 0.6 for inverter-controlled units.',
          maxTemperatureSupplied:
            'The highest supplied temperature is the warmest supply temperature the heat pump can deliver to the heating system, in °C. Heat pumps lose efficiency at high supply temperatures. Used to assess whether the pump covers the temperature demand, for example for radiators that require a high temperature.',
          cop: "COP (Coefficient of Performance) is the heat pump's efficiency for heating: the ratio of delivered heat to electricity supplied. A COP of 3 means three times as much heat as electricity. Enter a specific value if you have one, otherwise standard values are used.",
          eer: "EER (Energy Efficiency Ratio) is the heat pump's efficiency for cooling: the ratio of delivered cooling to electricity supplied. An EER of 3 means three times as much cooling as electricity. Used when the heat pump is ticked for cooling. If left empty, the COP is used.",
          liquidSource:
            'The liquid source is the medium the liquid-to-water heat pump draws energy from via a collector, for example ground, rock, fresh water or sea water. The source affects the available source temperature and thereby the efficiency.',
          sourceTemperature:
            'The source temperature is the temperature of the liquid coming from the source into the heat pump, in °C. A higher source temperature gives better efficiency. Enter it if it is known from measurement or a ground survey.',
          sourceYearlyAverageTemperature:
            'The yearly average temperature is the average temperature of the source over a year, in °C. It is used as a baseline for calculating the heat pump seasonal performance. Enter it if it is known, otherwise typical values for the selected source are used.',
          liquidMaxTemperature:
            'This is the highest temperature the source reaches during the year, in °C. Together with the yearly average and amplitude it describes how the source temperature swings through the season, which affects the efficiency.',
          sourceTemperatureAmplitude:
            'The temperature amplitude describes how much the source temperature swings above and below the yearly average over the year, in °C. A larger amplitude means a larger seasonal variation in efficiency. Enter it if it is known, otherwise typical values for the source are used.',
          energyWellDimensioning:
            'Guideline values for the yearly average temperature and amplitude of energy wells (boreholes). Select the description that best matches how the well is used and dimensioned; the temperature values are then set automatically based on the local climate.',
          waterSourceDepth:
            'Guideline values for the yearly average temperature and amplitude of heat extraction in sea or fresh water. Select the depth of the heat extraction; the temperature values are then set automatically based on the local climate.',
        },
        districtHeating: {
          maxCapacity:
            'The maximum capacity is the highest power the district heating or cooling connection can deliver to the building, in kW. Enter it if it is known from documentation or plant data. Used to assess whether the source covers the power demand.',
          efficiency:
            'The efficiency describes how much of the energy delivered from the district heating network actually becomes useful heat in the building, after losses in the exchanger and distribution. Enter an overall value if you want to override the standard values for the plant.',
          temperatureFromSource:
            'The temperature from source is the supply temperature the district heating or cooling network delivers to the heat exchanger, in °C. Enter it if it is known. Gives a more realistic description of the plant operation and efficiency.',
          heatExchangerLoss:
            "The heat exchanger transfers energy from the district heating network to the building's own system, and some energy is lost on the way. Select the insulation level and outlet temperature range to describe the loss in more detail, or enter the loss coefficient directly if a documented value exists. Leave the fields empty to use standard values.",
          distributionHeatLoss:
            'Distribution heat loss is the energy lost in the pipe network between the plant and the building system. Select the supply temperature range for the distribution to describe the loss in more detail, or enter the loss coefficient directly if it is known. Leave the fields empty to use standard values.',
          coolingHeatExchanger:
            'The cooling heat exchanger transfers cooling from district cooling or the chiller plant to the building system, and some energy is lost. Select the insulation level if the cooling side should be modelled in more detail, or enter the U-value directly if it is known. Leave the fields empty to use standard values.',
          co2eEmissionFactor:
            "The CO₂e emission factor indicates how much greenhouse gas emission accompanies the energy from district heating or cooling, given in grams per kWh. Enter a specific value if you want to use the network's actual emission figure. It only affects the emission accounting, not the energy demand itself.",
          energyPrice:
            'The energy price is the cost per kWh of energy delivered from district heating or cooling. Enter a specific price if you want cost calculations with real prices, otherwise the field can be left empty. It only affects cost figures, not energy or emissions.',
        },
        combustionBoiler: {
          boilerType:
            'The boiler type describes what kind of combustion boiler the plant is, for example oil, gas or biomass. Select the one that best describes the plant. The choice controls standard assumptions such as efficiency and emission level.',
          heatingCapacity:
            "The heating capacity is the boiler's nominal heat output in kW, i.e. how much power it can deliver under normal conditions. This is the main value for assessing whether the boiler covers the power demand.",
          performance:
            "Performance indicates the boiler's nominal efficiency level, i.e. how efficiently the fuel is converted into useful heat. Select or enter the level if it is documented for the boiler, otherwise standard values are used.",
          fuelSource:
            'The fuel source is what the boiler burns, for example oil, gas or biofuel. The choice determines the standard energy content and emission factor for the fuel, and thereby affects both the energy and emission calculation.',
          fuelEnergyContent:
            'The energy content is how much energy the fuel contains per unit, for example kWh per litre. Enter a specific value if you want more precise assumptions than the standard values for the selected fuel.',
          co2eEmissionFactor:
            'The CO₂e emission factor indicates how much greenhouse gas emission burning the fuel produces, given in grams per kWh. Enter a specific value if you want a precise emission calculation. It only affects the emission accounting, not the energy demand.',
          efficiencyAtLowerLoad:
            "This is the boiler's heat output when it runs at about 30% of full load, in kilowatts. The value is required together with the full load value so the simulation can model the boiler's part load behaviour.",
          effectAtFullLoad:
            "This is the boiler's heat output at full load in kilowatts, i.e. how much power it delivers when running at maximum. The value is required for the simulation to dimension the boiler correctly.",
          isCondensing:
            'A condensing boiler also recovers the heat in the water vapour of the flue gas, achieving a higher efficiency than a conventional boiler. Tick this if the boiler is condensing.',
          isCHP:
            'A combined heat and power (CHP) plant produces both electricity and heat at the same time from the same fuel, giving high overall energy utilisation. Tick this if the plant is a CHP plant; dedicated CHP settings then appear.',
          scheduleStart:
            "The start time is when the boiler's operating season begins, given as day and month. Fill this in if the boiler does not run continuously or does not follow the standard operating profile, for example a boiler used only during the heating season.",
          scheduleEnd:
            "The end time is when the boiler's operating season ends, given as day and month. Filled in together with the start time to describe the period the boiler is in operation.",
          seasonalMeanTemp:
            'The seasonal mean temperature is the average outdoor temperature over the operating season, in °C. Enter it if the plant should be described with more detailed temperature assumptions than the standard.',
          temperatureSupply:
            'The supply temperature is the temperature of the water the boiler sends out to the heating system, in °C. A lower supply temperature often gives better efficiency, especially for condensing boilers. Enter it if it is known.',
          temperatureReturn:
            'The return temperature is the temperature of the water coming back to the boiler from the heating system, in °C. For condensing boilers a low return temperature is important to achieve condensation and high efficiency. Enter it if it is known.',
        },
        electricBoiler: {
          maxCapacity:
            'The maximum capacity is the highest power the electric boiler can deliver, in kW. This is the main value for assessing whether the boiler covers the power demand.',
          insulationGrade:
            'The insulation grade describes how well the boiler itself is insulated, and determines how much heat it loses to the surroundings while it is on. Select a grade if you want more precise modelling of the heat loss from the boiler.',
          efficiency:
            'The efficiency describes how much of the supplied electricity becomes useful heat, after losses in the boiler. Electric boilers normally have a high efficiency. Enter a value if you want to override the standard assumptions.',
          co2eEmissionFactor:
            'The CO₂e emission factor indicates how much greenhouse gas emission accompanies the electricity the boiler uses, given in grams per kWh. Enter a specific value if you want a precise emission calculation. It only affects the emission accounting, not the energy demand.',
          temperatureSupply:
            'The supply temperature is the temperature of the water the boiler sends out to the heating system, in °C. It affects which heat emitters the boiler can serve and the heat loss in the system. Enter it if it is known.',
          temperatureReturn:
            'The return temperature is the temperature of the water coming back to the boiler from the heating system, in °C. Together with the supply temperature it describes the temperature level in the system. Enter it if it is known.',
        },
        solarThermal: {
          solarType:
            'The solar collector type describes the collector principle, for example flat plate or vacuum tube. Vacuum tube collectors generally perform better at low outdoor temperatures. The choice affects the standard yield assumptions for the plant.',
          azimuth:
            'The azimuth is the compass orientation of the collectors in degrees, where 0 typically means due south. It describes which way the collectors face and strongly affects how much solar energy they capture.',
          tilt: 'The tilt is the angle of the collectors from horizontal in degrees, where 0 is flat and 90 is vertical. Together with the azimuth it determines how much solar radiation the collectors receive over the year.',
          adjustmentFactor:
            'The adjustment factor scales the calculated solar yield up or down to account for local conditions such as shading or soiling. Enter a value only if you want to correct the standard calculation; otherwise leave it empty.',
          performance:
            'Performance indicates the nominal efficiency or capacity level of the solar thermal plant, i.e. how effectively the collectors convert solar radiation into useful heat. Select or enter the level if it is known from documentation, otherwise standard values are used.',
          pipeLength:
            'The pipe length is the total length of pipes between the collectors and the storage tank, in metres. Longer pipes lose more heat. Enter it together with the pipe insulation type if you want to calculate pipe losses in more detail.',
          pipeInsulationType:
            'The pipe insulation type describes how well the pipes between the collectors and the tank are insulated, which sets the standard heat loss per metre of pipe. Select it if known, otherwise a standard value is used.',
          pipeUValue:
            'The pipe U-value indicates how much heat the collector-loop pipes lose per metre and degree of temperature difference (W/mK). A lower value means better insulation. Enter it if it is known, otherwise it is derived from the selected pipe insulation type.',
          collectorArea:
            'The collector area is the total active area of the solar collectors, in m². This is a key input for the production calculation, since more collector area gives more solar energy.',
          tankSpiralPlacementFactor:
            'The tank spiral placement factor describes where in the storage tank the solar heat exchanger coil sits, which affects how effectively the solar heat is transferred to the water. Enter a value if you have detailed data for the system, otherwise a standard value is used.',
          temperatureColdWater:
            'The cold water temperature is the temperature of the water supplied to the system before it is heated by the collectors, in °C. A lower cold water temperature gives a higher solar yield. Enter a value if you want to override the standard value.',
          percentageOfCollectorLoopOutside:
            'This is how much of the collector loop (pipes and components) is located outdoors, as a percentage. Pipes that run outdoors lose more heat. Enter a value if this is relevant for the loss calculation.',
          collectorSpecs:
            "These values make up the collector's efficiency curve, taken from the product datasheet or the Solar Keymark certificate. η₀ is the optical efficiency at no temperature difference, while the heat-loss coefficients a₁ and a₂ describe how quickly the efficiency drops as the collector gets warmer than its surroundings. Enter the values if documentation exists, otherwise standard values are used.",
          snowCoverageMonths:
            'Select which months the solar collectors are normally snow-covered. In these months the collectors produce little or no energy, so this gives a more realistic annual calculation where snow affects production.',
          insulationFactorForCollectionLoop:
            'The insulation factor for the collection loop describes how well the pipes and components in the solar loop are insulated overall. Select a level if you want a more precise calculation of the heat loss in the loop, otherwise a standard value is used.',
        },
        otherSource: {
          fuelType:
            'The fuel type is the energy carrier this supply uses, for example oil, gas, biofuel, district heating or electricity. The choice determines the standard emission factor and how the energy is accounted for.',
          maxCapacity:
            'The maximum capacity is the highest power this energy source can deliver, in kW. This is the main value for assessing whether the source covers the power demand.',
          efficiency:
            'The efficiency describes how much of the supplied energy becomes useful heat or cooling, after losses in the source. Enter a value if it is known, otherwise a standard assumption is used.',
          co2eEmissionFactor:
            'The CO₂e emission factor indicates how much greenhouse gas emission accompanies the energy this source uses, given in grams per kWh. Enter a specific value if you want a precise emission calculation. It only affects the emission accounting, not the energy demand.',
          energyPrice:
            'The energy price is the cost per kWh of energy from this source. Enter a specific price if you want cost calculations with real prices, otherwise the field can be left empty. It only affects cost figures, not energy or emissions.',
        },
        accumulationTank: {
          purpose:
            'An accumulation tank stores heat or cooling so that production and consumption can be shifted in time. Select the purpose, for example heat storage, cold storage or hot water, describing the function the tank has in the system.',
          volume:
            "The volume is the tank's storage capacity in litres, and is the main parameter for how much heat or cooling the tank can store. Enter it if an accumulation tank exists.",
          insulation:
            'The insulation level describes how well the tank is insulated, and determines how much heat is lost to the surroundings. Select the level or type if known. Used to estimate or override the heat loss from the tank.',
          heatLoss:
            'Heat loss (W/K) indicates how much power the tank loses to the surroundings per degree of temperature difference. Enter a documented value if you have one. Can normally be left empty if volume and standard insulation are sufficient.',
          setpoint:
            'The setpoint temperature is the desired storage temperature in the tank, in °C. It controls how hot or cold the medium is kept and affects both losses and capacity.',
          maxTemp:
            'The maximum temperature is the highest temperature the tank is allowed to reach, in °C. Enter it if this is known or relevant for the control.',
          minTemp:
            'The minimum temperature is the lowest temperature the tank is allowed to fall to, in °C. Enter it if this is known or relevant for the control.',
          minTempDhw:
            'The minimum temperature is the lowest temperature the tank is allowed to fall to, in °C. For domestic hot water tanks it should be kept at 70 °C or higher to prevent legionella bacteria buildup.',
        },
      },
      excessEnergy: {
        medium:
          'The medium is what the excess energy flows in: air, for example an exhaust duct, or liquid, for example a waste-water pipe. The choice determines which fields are shown and how the available energy is calculated.',
        name: 'Give the excess energy source a clear name. The name is used to identify potential sources for recovery, for example exhaust air from a kitchen or waste heat from a process.',
        ductShape:
          'Select the medium and duct shape for the source. The duct shape, for example round or rectangular, is used together with the dimensions to calculate the cross-section and thereby the available energy flow when the excess energy is taken from air.',
        ductWidthDiameter:
          'Enter the width or diameter of the duct in mm if it is known. Used together with the duct shape, and possibly height, air velocity or volume flow, to calculate how much air flows through the duct.',
        height:
          'Enter the duct height in mm if the duct shape requires it, for example for a rectangular duct. Together with the width it gives the cross-section of the duct.',
        airspeed:
          'The air velocity is how fast the air flows through the duct, in m/s. Enter it if the volume flow is not known directly; the air flow is then calculated from the velocity and the duct cross-section.',
        exhaustTempAir:
          'The exhaust temperature is the temperature of the air stream the excess energy is taken from, in °C. The temperature is decisive for assessing how much energy can be recovered; a higher temperature gives more available heat.',
        exhaustTempLiquid:
          'Enter the temperature of the liquid stream the excess energy is taken from, in °C. The temperature is decisive for assessing how much energy can be recovered from the liquid.',
        volumeFlowAir:
          'The volume flow is how much air passes per hour, in m³/h. Together with the temperature it determines how much energy is available for recovery. Enter it if it is known, otherwise it can be calculated from the duct geometry and air velocity.',
        volumeFlowLiquid:
          'The volume flow is how much liquid passes per hour, in L/h. Together with the temperature it determines how much energy is available for recovery. Enter it if it is known.',
      },
      solarPv: {
        panelAngle:
          'The tilt of the solar panels measured from horizontal, in degrees. A flat panel laid on the roof is 0° and a panel standing upright is 90°. The tilt decides how directly sunlight hits the panels as the sun moves through the sky over the year, so it strongly influences the estimated annual energy production.',
        name: 'A clear, recognisable name for this solar PV installation. It is used to identify the array in lists, results and reports, which is helpful when a building has several separate PV systems.',
        panelArea:
          'The active surface area of the solar panels, in square metres. Enter either the area of a single panel or the combined area of the whole array, depending on how this installation is registered, and make Quantity match that choice. Together with the panel efficiency, area is one of the central inputs that decides how much electricity the system can produce.',
        quantity:
          'The number of solar panels in this installation, if known. It is combined with the panel area and efficiency to estimate the installed capacity, so it must match how the area was entered: use the panel count when Panel Area is per panel, and 1 when Panel Area is the total array area.',
        yearInstalled:
          'The year the solar PV installation was commissioned. It is recorded for the inspection, but the calculation does not use it: panel ageing is always calculated over a fixed 30-year lifetime, using the degradation loss per year.',
        soilingLocation:
          'The site environment that determines how quickly dust, pollen and pollution build up on the panels. Choosing a location, for example urban, rural or another exposure, loads a standard monthly soiling profile for that environment, which you can then fine-tune per month below.',
        monthlySoiling:
          'Soiling is the loss of output caused by dirt, dust, snow and pollution on the panel surface, and it varies through the year. Selecting a location fills in a simplified standard profile; enter your own value for each month here if you want to override that profile with site-specific figures.',
        cellType:
          'The photovoltaic technology of the panels, for example monocrystalline or polycrystalline silicon. The cell type determines how efficiently the panels convert sunlight into electricity, so selecting it sets a representative default nominal efficiency that you can still adjust.',
        nominalEfficiency:
          'The share of incoming solar energy the panels convert to electricity under standard test conditions, given as a decimal (for example 0.20 for 20%). Enter the documented value from the datasheet if it is known; otherwise the default from the chosen cell type is used.',
        panelAzimuth:
          "The compass direction the panels face, in degrees from 0 to 360 (with south around 180° in the northern hemisphere). The azimuth, together with the tilt, decides how much of the sun's path the panels capture over the day and year, so it directly affects the estimated production.",
        shadingFactor:
          'The share of sunlight the panels lose to shading from nearby buildings, terrain or vegetation, from 0 to 1. 0 means no shading and 1 means fully shaded, so the default 0.05 is a 5 % loss. The value is recorded, but the calculation does not use it yet.',
        mountingType:
          'How the panels are mounted, for example roof-integrated or free-standing with an air gap. The mounting type governs how well the panels are cooled by surrounding air, which feeds the more detailed panel-temperature and loss model. Select it if you want that level of detail.',
        windExposure:
          'How exposed the array is to wind. Wind cools the panels and lowers their operating temperature, which improves efficiency, so this is used together with the mounting type in the more detailed panel-temperature model. Select it if it is known.',
        heatLossFactor:
          'How readily the panels shed heat to their surroundings, in W/m²K. It is normally derived from the mounting type and wind exposure, but you can enter a value here to override those standard assumptions and control how panel temperature is modelled.',
        inverterEfficiency:
          'The share of DC power from the panels that the inverter delivers as usable AC power, as a decimal (for example 0.945). It captures the conversion loss in the inverter; enter the documented datasheet value if it is known.',
        degradationLoss:
          "The gradual, permanent decline in panel output as the modules age, as a decimal (for example 0.005 for 0.5% per year). Enter it if you want to model the reduced performance over the system's lifetime more precisely.",
        inverterLoss:
          'Additional losses associated with the inverter and its operation, as a decimal. Use this when you want to record inverter-related losses separately from the inverter efficiency rather than folding them into a single figure.',
        transmissionProduct:
          "A combined factor (0-1) for the optical losses as light passes through the panel's cover glass and reaches the cells, such as reflection and absorption. Enter it as a decimal when the selected calculation method uses this term.",
        temperatureCoefficient:
          'How much the panel output changes with temperature, in %/°C. It is normally negative because output drops as the panels heat up above their rated test temperature. Enter the documented value from the datasheet if one exists for these panels.',
        incidentAngleModifier:
          'A factor (0-1) that accounts for the extra reflection losses when sunlight strikes the panel at an angle rather than head-on. Enter it as a decimal if you want to use the more detailed solar model that captures these angle-of-incidence effects.',
      },
      battery: {
        name: 'A clear, recognisable name for this battery. It is used to identify the battery in lists, results and reports, which is helpful when a building has several batteries.',
        maxCapacity:
          'The total amount of energy the battery can store, in kWh. This is the nameplate capacity of the battery system and sets the upper limit for how much solar surplus can be stored for later use.',
        maxChargePower:
          'The highest power the battery can charge with, in kW. It limits how quickly the battery can absorb solar surplus or grid power.',
        maxDischargePower:
          'The highest power the battery can deliver back to the building, in kW. It limits how much of the building load the battery can cover at any moment.',
        chargeEfficiency:
          'How large a share of the energy sent into the battery is actually stored, between 0 and 1. Leave it at 0 to use the typical lithium-ion default.',
        dischargeEfficiency:
          'How large a share of the stored energy is actually delivered when the battery discharges, between 0 and 1. Leave it at 0 to use the typical lithium-ion default.',
        minStateOfCharge:
          'The lowest level the battery is allowed to discharge to, as a share of full capacity between 0 and 1. Keeping a reserve protects battery lifetime. Leave it at 0 to use the default.',
        maxStateOfCharge:
          'The highest level the battery is allowed to charge to, as a share of full capacity between 0 and 1. Charging slightly below full protects battery lifetime. Leave it at 0 to use the default.',
        peakShavingThreshold:
          'The grid import the battery tries to keep the building below, in kW. When the building would draw more than this from the grid, the battery discharges to cover the difference. Leave it at 0 to disable peak shaving; the battery then only stores solar surplus and covers deficits.',
        preChargeStateOfCharge:
          'The level the battery pre-charges to from the grid in quiet hours, as a share of full capacity between 0 and 1, so it is ready for the next peak. Only used when a peak shaving threshold is set. Leave it at 0 to disable pre-charging.',
      },
    },
  },

  // Page titles
  title: 'Building Inspections',
  sidebarTitle: 'Inspection',
  new: 'New Building Inspection',
  edit: 'Edit Inspection',

  // List page
  fetchError: 'Failed to fetch building inspections.',
  noInspections: 'No building inspections found.',
  createNew: 'New Inspection',
  confirmDeleteTitle: 'Delete inspections',
  confirmDeleteDescription: 'Are you sure you want to delete this inspection?',
  duplicate: 'Duplicate',
  duplicating: 'Duplicating inspection...',
  duplicateSuccess: 'Inspection duplicated',
  duplicateError: 'Failed to duplicate inspection',
  renameInspection: 'Rename inspection',

  // Common. TODO: add way more common translations for all languages. Then compare to global common dash lang and start using those.
  common: {
    // generic
    custom: 'Custom',
    standard: 'Standard',
    other: 'Other',
    text: 'Text',
    from: 'From',
    to: 'To',
    hide: 'Hide',
    show: 'Show',
    horizontal: 'Horizontal',
    vertical: 'Vertical',
    addImage: 'Add image',
    confirmDeleteImage: 'Are you sure you want to delete this image?',
    generating: 'Generating...',
    indoorClimate: 'Indoor climate',
    score: 'Score',
    energyData: 'Energy data',
    air: 'Air',
    liquid: 'Liquid',
    round: 'Round',
    square: 'Square/Rectangular',

    // field labels
    quantity: 'Quantity',
    location: 'Location',
    priority: 'Priority',
    yearInstalled: 'Year Installed',
    medium: 'Medium',

    // section / navigation labels
    inspection: 'Inspection',
    info: 'Information',
    purpose: 'Purpose',
    module: 'Module',
    control: 'Control & Regulation',
    equipment: 'Equipment',
    hvacSystems: 'HVAC Systems',
    thermalZones: 'Thermal Zones',
    zones: 'Zones',
    heatingCooling: 'Heating & Cooling',
    heatingAndCooling: 'Heating & Cooling',
    heatingOnly: 'Heating only',
    domesticHotWater: 'Domestic Hot Water',
    centralEnergy: 'Central Energy',
    excessEnergy: 'Excess Energy',
    solarPv: 'Solar PV',
    solarThermal: 'Solar Thermal',
    solarCells: 'Solar Cells',
    attachments: 'Attachments',
    annualSimulation: 'Annual Simulation',
    energyLabel: 'Energy Label',
    actions: 'Actions',
    lighting: 'Lighting',
    report: 'Report',
    hvac: 'HVAC',
    windows: 'Windows',
    doors: 'Doors',
    gates: 'Gates',
    walls: 'Walls',
    exteriorWalls: 'Exterior Walls',
    floor: 'Floor to Ground',
    roof: 'Roof',
    basement: 'Basement construction',
    groundConditions: 'Ground Conditions',
    airLeakage: 'Air Leakage',
    thermalMass: 'Thermal Mass',
    internalLoads: 'Internal Loads',
    productionEquipment: 'Production Equipment',
    ground: 'Ground',
    zone: 'Zone',
    wall: 'Wall',
    window: 'Window',
    door: 'Door',
    gate: 'Gate',

    // climate
    climate: 'Climate',
    climatized: 'Climatized',
    partlyClimatized: 'Partly Climatized',
    unclimatized: 'Unclimatized',
    floorHeight: 'Floor Height (m)',

    // units and physical quantities
    area: 'Area (m²)',
    areaShort: 'Area',
    volume: 'Volume (L)',
    volumeShort: 'Volume',
    length: 'Length (m)',
    power: 'Power (kW)',
    powerShort: 'Power',
    uValue: 'U-Value (W/m²K)',
    uValueShort: 'U-value',
    coverageArea: 'Coverage Area (m²)',
    coverageAreaShort: 'Coverage area',
    coverageVolume: 'Coverage volume',
    heatingEffect: 'Heating Effect (kW)',
    coolingEffect: 'Cooling Effect (kW)',
    effect: 'Effect (kW)',
    cop: 'COP',
    eer: 'EER',
    seasonalCOP: 'Seasonal COP',
    seasonalEER: 'Seasonal EER',
    efficiency: 'Efficiency',
    systemEfficiency: 'System Efficiency (%)',
    co2eEmissionFactor: 'CO₂e Emission Factor (g/kWh)',
    temperatureSupply: 'Supply Temperature (°C)',
    temperatureReturn: 'Return Temperature (°C)',
    avgTemp: 'Avg temp',
    sfp: 'SFP',
    bra: 'BRA',
    airVolume: 'Air volume',
    heatRecovery: 'Heat recovery',
    panelArea: 'Panel area',
    cellType: 'Cell type',
    azimuth: 'Azimuth',
    value: 'Value',
    unit: 'Unit',

    // system / fuel
    systemType: 'System Type',
    systemTypeShort: 'System type',
    energySource: 'Energy Source',
    energySourceShort: 'Energy source',
    specificFuelType: 'Specific Fuel Type',
    selectSpecificFuelType: 'Select specific fuel...',
    inverterControlled: 'Inverter controlled',
    nominalLoad: 'Nominal Load',
    minimumLoad: 'Minimum Load',
    degradeCoeff: 'Degrade Coeff',
    heatPump: 'Heat Pump',
    electric: 'Electric',
    oil: 'Oil',
    gas: 'Gas',
    rock: 'Rock',
    coolingOnly: 'Cooling only',

    // performance / magnitude grades
    high: 'High',
    low: 'Low',
    normal: 'Normal',

    // dimensions
    width: 'Width (m)',
    height: 'Height (m)',
    depth: 'Depth (m)',

    // zone editor
    userDefined: 'User defined',
    calculator: 'Calculator',
    compass: {
      N: 'N',
      NE: 'NE',
      E: 'E',
      SE: 'SE',
      S: 'S',
      SW: 'SW',
      W: 'W',
      NW: 'NW',
    },
    directions: {
      n: 'North (0°)',
      ne: 'Northeast (45°)',
      e: 'East (90°)',
      se: 'Southeast (135°)',
      s: 'South (180°)',
      sw: 'Southwest (225°)',
      w: 'West (270°)',
      nw: 'Northwest (315°)',
    },
    structuralNodes: 'Structural Nodes',
    structuralNode: 'Structural node',
    addStructuralNode: 'Add structural node',
    copyStructuralNode: 'Copy structural node',
    confirmDeleteStructuralNode: 'Delete this structural node?',
    boundary: 'Boundary',
    boundaryInteriorAir: 'Interior air',
    boundaryExterior: 'Exterior',
    boundaryAdjacentZone: 'Adjacent zone',
    boundaryAdiabatic: 'Adiabatic',
    node: 'Node',
    addNode: 'Add node',
    copyNode: 'Copy node',
    confirmDeleteNode: 'Delete this node?',
    surfaceType: 'Surface type',
    surfaceCeiling: 'Ceiling',
    surfaceArea: 'Surface area (m\u00B2)',
    heatTransferCoeff: 'Heat transfer coeff (W/m\u00B2K)',
    totalMass: 'Total mass (kg/m\u00B3)',
    specificHeatCapacity: 'Specific heat capacity (J/kgK)',

    // insulation grades
    insulated: 'Insulated',
    uninsulated: 'Uninsulated',
    poorlyInsulated: 'Poorly insulated',
    normallyInsulated: 'Normally insulated',
    wellInsulated: 'Well insulated',
    veryWellInsulated: 'Very well insulated',
    selectInsulation: 'Select insulation...',
    selectPerformance: 'Select performance...',
    selectFuelType: 'Select fuel type...',

    // UI layout
    basicSettings: 'Basic Settings',
    additionalInfo: 'Additional Information',
    showAdvanced: 'Show Advanced',
    hideAdvanced: 'Hide Advanced',
    advanced: 'Advanced',

    settingUp: 'Setting up...',
    co2PerKwh: 'CO₂ per kWh [g]',
    gridFee: 'Grid Fee [NOK/kWh]',
    enovaSupport: 'Enova Support [%]',
    descriptionPlaceholder:
      'Describe the action in detail: what is being changed, why, and the expected outcome. Include a calculation of the estimated reduced energy use if the measure is carried out, along with an assessment of costs and consequences regarding moisture and indoor climate related to the measure.',
    electricityZone: 'Electricity Zone',
    selectZone: 'Select a zone...',
    runSimulation: 'Run Building Simulation',
    runningSimulation: 'Running Simulation...',
    simulationDescription: 'Runs hourly energy balance simulation',
    baselineProgress: 'Running baseline simulation... (1/2)',
    actionProgress: 'Running action simulation... (2/2)',
    baselineStarted: 'Baseline simulation started...',
    simulationStarted: 'Action simulation started...',
    noAnnualSimulation:
      'Run an annual simulation first to enable action comparison.',
    simulationResults: 'Simulation Results',
    lastSimulated: 'Last simulated',
    nettEnergyDemand: 'Nett Energy Demand',
    energyCategory: 'Energy Category',
    baselineKwh: 'Baseline (kWh)',
    actionKwh: 'Action (kWh)',
    savingsKwh: 'Savings (kWh)',
    baselineKwhM2: 'Baseline (kWh/m²)',
    actionKwhM2: 'Action (kWh/m²)',
    savingsPercent: 'Savings (%)',
    baseline: 'Baseline',
    afterAction: 'After Action',
    solarProduction: 'Solar production',
    savings: 'Savings',
    costSavings: 'Cost & savings',
    annualEnergySavings: 'Annual Energy Savings',
    kwhYear: 'kWh/year',
    energySavingsTooltip: 'Reduction in total energy demand from improvements',
    reduction: 'reduction',
    increase: 'increase',
    annualCostSavings: 'Annual Cost Savings',
    nokYear: 'NOK/year',
    costSavingsTooltip:
      'Total cost savings from reduced energy consumption and income from exported solar production',
    netCo2Reduction: 'Net CO₂ Reduction',
    kgCo2Year: 'kg CO₂/year',
    co2Tooltip: 'Using {value} g CO₂/kWh',
    tonnes: 'tonnes',
    paybackPeriod: 'Payback Period',
    paybackTooltip: 'Time to recover the initial investment',
    years: 'years',
    investmentLabel: 'Investment: {value} NOK',
    noInvestmentCost: 'No investment cost specified',
    beforeExisting: 'Before - Existing System',
    afterNew: 'After - New System',
    investment: 'Investment',
    installationType: 'Installation Type',
    simple: 'Simple',
    complex: 'Complex',
    deliveredEnergy: 'Delivered Energy',
    roiTitle: 'Return on Investment Timeline',
    roiCumulativeSavings: 'Cumulative Savings',
    roiNetValue: 'Net Value (after investment)',
    roiYears: 'Years',
    roiBreakEven:
      'Break-even point occurs when net value crosses zero (year {value})',

    // misc
    presenceControl: 'Presence control',
    distributionLosses: 'Distribution and Accumulation Losses',
    installationCost: 'Installation Cost [NOK]',
    installationCostShort: 'Installation cost',
    noZones:
      'No thermal zones found. Add thermal zones in the Thermal Zones section first.',

    // energy source labels duplicated across heatingCooling,
    // productionEquipment, hvac and centralEnergy
    energySources: {
      electricity: 'Electricity',
      heatpumpAirToAir: 'Heatpump Air-to-Air',
      fossilOil: 'Fossil Oil',
      fossilGas: 'Fossil Gas',
      fossilSolidFuel: 'Fossil Solid Fuel',
      bioOil: 'Bio Oil',
      bioGas: 'Bio Gas',
      bioSolidFuel: 'Bio Solid Fuel',
      districtHeating: 'District Heating',
      districtCooling: 'District Cooling',
      otherSources: 'Other Sources',
    },

    // schedule block duplicated between thermalZones.schedule and
    // productionEquipment
    schedule: {
      operationalSchedule: 'Operational Schedule',
      addSchedule: 'Add schedule',
      clickToToggle: 'Click hours to toggle on/off',
      avgTemp: 'Avg temp',
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
      hours: 'Hours',
      weekly: 'Weekly',
      annual: 'Annual',
      on: 'ON',
      off: 'OFF',
      hoursPerDayUnit: 'h/day',
      hoursPerWeekUnit: 'h/week',
      hoursPerYearUnit: 'h/year',
      setpointPerHour: 'Setpoint temperature per hour',
      setpointInfoTooltip:
        'Use ON/OFF to control operating hours. Setpoint values for ON hours are used in calculations. OFF hours are non-operating.',
      copyToWeekdays: 'Copy to weekdays',
      copyToWeekends: 'Copy to weekends',
      resetToStandards: 'Reset to',
      confirmDeleteSchedule: 'Delete this schedule?',
      fromMonth: 'From',
      toMonth: 'To',
    },
  },

  // List page labels
  lists: {
    quantity: 'Quantity',
    totalArea: 'Total (m²)',
    thermalZones: {
      duplicateZone: 'Duplicate zone',
      copySuffix: 'copy',
      duplicateSuccess: 'Zone duplicated successfully',
      duplicateError: 'Failed to duplicate zone',
    },
    exteriorWalls: {
      addFirst: 'Click to add your first exterior wall',
      addWall: 'Add Wall',
      duplicateWall: 'Duplicate wall',
    },
    windows: {
      addFirst: 'Click to add your first window',
      addWindow: 'Add Window',
    },
    doors: {
      addFirst: 'Click to add your first door',
      addDoor: 'Add Door',
    },
    gates: {
      addFirst: 'Click to add your first gate',
      addGate: 'Add Gate',
    },
    productionEquipment: {
      addFirst: 'Click to add your first production equipment',
      addEquipment: 'Add Production Equipment',
    },
    hvac: {
      addFirst: 'Click to add your first HVAC unit',
      addHvac: 'Add HVAC',
    },
    heatingCooling: {
      addFirstHeating: 'Click to add a heating or cooling system',
      addHeating: 'Add System',
    },
    domesticHotWater: {
      add: 'Add Domestic Hot Water',
      addFirst: 'Click to add domestic hot water',
    },
    centralEnergy: {
      add: 'Add Central Energy Module',
      addFirst: 'Click to add a central energy module',
    },
    excessEnergy: {
      add: 'Add Excess Energy Module',
      addFirst: 'Click to add an excess energy module',
    },
    solarPv: {
      add: 'Add Solar PV Module',
      addFirst: 'Click to add a solar PV module',
    },
    batteries: {
      add: 'Add Battery',
      addFirst: 'Click to add a battery',
    },
    attachments: {
      noInspectionFound: 'No inspection found',
    },
  },

  // Edit page - Form fields
  inspector: 'Inspector',
  selectBuilding: 'Select Building',
  selectedBuilding: 'Building',
  selectBuildingPlaceholder: 'Select building...',
  notes: 'Notes',

  // Edit page - Map panel
  advancedComingSoon: 'Under development',
  mapPlaceholder: 'Sastech Map',

  // Status options
  statusDraft: 'Draft',
  statusInReview: 'In Review',
  statusCompleted: 'Completed',

  // Validation & feedback
  buildingRequired: 'Please select a building',
  gpsRequired: 'The selected building must have a GPS location',
  createSuccess: 'Inspection created successfully',
  updateSuccess: 'Inspection updated successfully',
  saved: 'saved',
  saveError: 'Failed to save inspection',
  loadError: 'Failed to load inspection data',

  // Delete dialog
  deleteTitle: 'Delete Inspection',
  deleteConfirm: 'Are you sure you want to delete this inspection?',
  deleteSuccess: 'Inspection deleted successfully',
  deleteError: 'Failed to delete inspection',

  // Zone edit
  zoneName: 'Zone name',
  zoneCategory: 'Zone Category',
  buildingSubcategory: 'Building Subcategory',
  zoneSaved: 'Zone saved',
  standardElementsUpdated: 'Updated {count} elements to {tek}',
  braRequired: 'BRA greater than 0 is required',
  basementAreaRequired: 'Basement floor area greater than 0 is required',
  categoryRequired: 'Please select a building category',
  yearRequired: 'Please enter the construction year',
  floorsRequired: 'Please enter the number of floors',
  zoneCategoryRequired: 'Please select a zone category',
  subcategoryRequired: 'Please select a building subcategory',
  zoneClimateRequired: 'Please select a zone climate',
  floorHeightRequired: 'Floor height greater than 0 is required',
  airLeakageN50Required: 'Air leakage number (n50) greater than 0 is required',
  thermalBridgePsiRequired: 'A normalized thermal bridge value is required',
  panelAreaRequired: 'Panel area greater than 0 is required',
  yearInstalledRequired: 'Year installed is required',
  soilingLocationRequired: 'Please select a soiling location',
  cellTypeRequired: 'Please select a cell type',
  deleteZone: 'Delete zone',
  deleteZoneWarning: 'This action cannot be undone',
  confirmDeleteZone: 'Delete this zone?',
  indoorTemp: 'Start Temperature (°C)',
  zoneClimate: 'Zone Climate',
  thermalBridgeCoeff: 'Structural System (Thermal Bridge)',
  thermalBridgePsiValue: 'Normalized Thermal Bridge Coefficient ψ (W/(m²·K))',
  thermalBridgeCoeffTypes: {
    custom: 'Custom',
    wood: 'Building with wood structural system',
    concrete_10cm:
      'Building with concrete/masonry/steel structural system and 10 cm thermal break in facades',
    concrete_5cm:
      'Building with concrete/masonry/steel structural system and 5 cm thermal break in facades',
  },
  addZone: 'Add Zone',
  addFirstZone: 'Click to add your first zone',
  image: 'Image',
  upload: 'Upload',

  // Heating/Cooling edit pages
  heatingCooling: {
    deleteModule: 'Delete module',
    confirmDeleteModule: 'Delete this module?',
    deleteHeating: 'Delete heating module',
    deleteCooling: 'Delete cooling module',
    confirmDeleteHeating: 'Delete this heating module?',
    confirmDeleteCooling: 'Delete this cooling module?',
    systemType: 'Type',
    priorityHelp: '1 = highest priority (covers demand first)',
    maxTemperatureSupplied: 'Max Temperature Supplied (°C)',
    systemTypeRequired: 'Please select a system type',
    energySourceRequired: 'Please select an energy source',
    heatingEffectRequired: 'Heating effect greater than 0 is required',
    coolingEffectRequired: 'Cooling effect greater than 0 is required',
    systemEfficiencyRequired: 'System efficiency greater than 0 is required',
    copRequired: 'COP greater than 0 is required',
    eerRequired: 'EER greater than 0 is required',
  },

  domesticHotWater: {
    deleteDhw: 'Delete domestic hot water',
    confirmDelete: 'Delete this domestic hot water module?',
    heaterType: 'Heater Type',
    volume: 'Volume (L)',
    setpointTemperature: 'Setpoint Temperature (°C)',
    heatingElementPower: 'Heating Element Power (kW)',
    heaterTypes: {
      electric: 'Electric',
      centralHeating: 'Central Heating',
    },
    typeSection: 'Type',
    consumptionSection: 'Consumption',
    distributionSection: 'Distribution System',
    greyWaterSection: 'Grey Water Heat Recovery',
    tapFrequency: 'Tap Frequency',
    tapCyclesPerDay: 'Tap Cycles Per Day',
    hasCirculation: 'Hot water system with circulation',
    pipeSpecificLength: 'Specific DHW pipe length',
    circulationPipeLength: 'Specific circulation pipe length',
    greyWaterRecoveryType: 'Recovery Unit Type',
    greyWaterEfficiencyGrade: 'Instantaneous Efficiency',
    greyWaterShareType: 'Share of Hot Water for Recovery',
    greyWaterConnectionType: 'Connection Variant',
    greyWaterPatternType: 'Tap Pattern',
    greyWaterHelperText:
      'Recovered energy from grey water is deducted from the total energy demand for domestic hot water heating.',
    tapFrequencyTypes: {
      high: 'High tap frequency',
      normal: 'Normal tap frequency',
      low: 'Low tap frequency',
      perDay: '/day',
    },
    dhwPipeLengthTypes: {
      residentialLong: 'Residential, long pipe runs',
      residentialNormal: 'Residential, normal pipe runs',
      residentialShort: 'Residential, short pipe runs',
      commercialLong: 'Commercial, long pipe runs',
      commercialNormal: 'Commercial, normal pipe runs',
      commercialShort: 'Commercial, short pipe runs',
    },
    circulationPipeLengthTypes: {
      circulationLong: 'Circulation system, long pipe runs',
      circulationNormal: 'Circulation system, normal pipe runs',
      circulationShort: 'Circulation system, short pipe runs',
    },
    greyWaterRecoveryTypes: {
      verticalPipe: 'Vertical pipe heat exchanger',
      horizontalPipe: 'Horizontal heat exchanger',
      tankRecovery: 'Tank-based heat recovery',
    },
    greyWaterEfficiencyGrades: {
      low: 'Low efficiency',
      normal: 'Normal efficiency',
      high: 'High efficiency',
    },
    greyWaterShareTypes: {
      allShowers: 'All showers to recovery',
      halfShowers: '50% of showers to recovery',
      thirdShowers: '30% of showers to recovery',
    },
    greyWaterConnectionTypes: {
      hotWaterTankOnly: 'Connected to hot water tank only',
      coldWaterShowerOnly: 'Connected to cold water shower only',
      hotWaterTankAndColdWaterShower:
        'Connected to hot water tank and cold water shower',
    },
    greyWaterPatternTypes: {
      residentialWithAccumulation: 'Residential with grey water accumulation',
      residentialWithoutAccumulation:
        'Residential without grey water accumulation',
      showerWithoutAccumulation:
        'Shower facility without grey water accumulation',
      showerWithAccumulation: 'Shower facility with grey water accumulation',
    },
  },

  // Pipe Link configuration
  pipeLink: {
    localEnergySources: 'Local Energy Sources',
    centralEnergySources: 'Central Energy Sources',
    noCentralModules: 'No central energy modules configured',
    pipeConfiguration: 'Pipe Configuration',
    spp: 'SPP (kW/(l/s))',
    constantFlow: 'Constant Flow',
    variableFlow: 'Variable Flow',
    lengthOfPipe: 'Pipe length (m)',
    percentageInHeatedSpace: '% in Heated Space',
    avgTempOutsideHeatedSpace: 'Avg Temp Outside Heated Space (°C)',
    // Dropdown labels
    waterSystemQuality: 'Water System Quality',
    waterDistributionType: 'Water Distribution Type',
    pipeInsulationType: 'Pipe Insulation',
    // Water system quality options (NS 3031:2025 Table E.1)
    qualityOptions: {
      poor: 'Poor Water Distribution',
      normal: 'Normal Water Distribution',
      good: 'Good Water Distribution',
    },
    // Water distribution system types (NS 3031:2025 Table E.1)
    distributionTypes: {
      radiatorFloorHeating: 'Heating system, radiator/floor heating',
      heatingBattery: 'Heating system, heating battery in ventilation',
      localCoolingCeilingBaffles:
        'Cooling system, local cooling/ceiling baffles',
      centralCoolingBattery:
        'Cooling system, central cooling battery in ventilation',
      dhwCirculation: 'DHW circulation',
      energyWellBrine: 'Energy well, brine',
    },
    // Pipe insulation types (NS 3031:2025 Table H.2)
    insulationTypes: {
      uninsulated: 'Uninsulated pipes',
      poorlyInsulated: 'Poorly insulated pipes',
      normallyInsulated: 'Normally insulated pipes',
      wellInsulated: 'Well insulated pipes',
      veryWellInsulated: 'Very well insulated pipes',
    },
    // Distribution pipe length type (NS 3031:2025 Table H.1)
    distributionPipeLengthType: 'Pipe length per BRA',
    distributionPipeLengthTypes: {
      localAndCentralLong: 'Both local and central heating, long pipe runs',
      localAndCentralNormal:
        'Both local and central heating, normally long pipe runs',
      centralOnlyLong: 'Only central heating, long pipe runs',
      centralOnlyShort: 'Only central heating, short pipe runs',
    },
    temperatureCannotBeZero: 'Temperature cannot be 0',
    pipeTemperatureZeroWarning: 'Supply and/or return temperature is set to 0.',
  },

  // Production Equipment edit page
  productionEquipment: {
    deleteEquipment: 'Delete production equipment',
    confirmDelete: 'Delete this production equipment?',
    eligibleForHarvesting: 'Eligible for energy harvesting',
    eligibleForHarvestingHelp:
      'Mark this equipment as eligible for excess energy harvesting',
  },

  // Central Energy Module
  centralEnergy: {
    // Basic settings
    mode: 'Mode',
    heatPumpTypeRequired: 'Each heat pump needs a heat pump type',
    heatPumpNominalLoadRequired:
      'Each heat pump needs a nominal load greater than 0',
    heatPumpMinimumLoadRequired:
      'Each heat pump needs a minimum load greater than 0',
    combustionBoilerTypeRequired: 'Each combustion boiler needs a boiler type',
    combustionBoilerFuelSourceRequired:
      'Each combustion boiler needs a fuel source',
    fillRequiredFields: 'Fill in all the required fields',
    requiredFieldsNotSaved:
      'Changes are not saved until all required fields are filled',
    solarThermalSolarTypeRequired:
      'Each solar thermal source needs a solar collector type',
    solarThermalCollectorAreaRequired:
      'Each solar thermal source needs a collector area greater than 0',
    otherSourceMaxCapacityRequired:
      'Each other source needs a max capacity greater than 0',
    otherSourceEfficiencyRequired:
      'Each other source needs an efficiency greater than 0',
    heatingAndHotWater: 'Heating and hot water',
    thermalZone: 'Thermal Zone',
    thermalZoneHelp: 'Link to a thermal zone for ambient temperature',
    noZone: 'None (standalone)',
    selectZone: 'Select zone...',
    ambientTemperature: 'Ambient Temperature',
    ambientTempHelp: 'Temperature around the module when not linked to a zone',
    moduleName: 'Central Energy Module',
    deleteModule: 'Delete central energy module',
    confirmDelete:
      'Delete this central energy module and all its energy sources?',
    notLinkedWarning:
      'This module is not linked to any equipment. Add it as an energy source in heating/cooling, ventilation, domestic hot water, or production equipment.',
    notLinkedListWarning:
      '{name} is not linked to any equipment. Add it as an energy source to use it.',
    notesAttachments: 'Notes & Attachments',
    energySources: 'Energy Sources',
    tanks: 'Tanks',
    performance: 'Performance',
    selectPerformance: 'Select performance...',

    // Accumulation tanks
    accumulationTanks: 'Accumulation Tanks',
    addTank: 'Add Tank',
    confirmDeleteTank: 'Delete this accumulation tank?',
    noTanks: 'No accumulation tanks configured. Click to add one.',
    dhwTank: 'DHW (Hot Water)',
    heatingTank: 'Heating Buffer',
    coolingTank: 'Cooling Storage',
    insulation: 'Insulation',
    setpoint: 'Setpoint (°C)',
    maxTemp: 'Max Temp (°C)',
    minTemp: 'Min Temp (°C)',
    heatLoss: 'Heat Loss (W/K)',

    // Electric boiler insulation (Table J.2)
    boilerInsulation: 'Boiler Insulation Grade',
    boilerInsulationVeryLow: 'Very poorly insulated',
    selectBoilerInsulation: 'Select insulation...',

    // Energy source configuration
    energySourceConfig: 'Energy Source Configuration',
    sourceType: 'Energy Source Type',
    selectType: 'Select energy source type...',
    sourceName: 'Energy source name',
    usage: 'Usage',
    dhw: 'DHW',
    addToList: 'Add to List',

    // Priority list
    priorityList: 'Priority List',
    dragToReorder: 'Click to edit. Drag to reorder',
    clickToEdit: 'Click a source to edit it',
    confirmDeleteSource: 'Delete this energy source?',
    noPrioritySources: 'No energy sources added yet',
    noSources:
      'No energy sources added yet. Use the form above to add sources.',

    // Energy source types
    districtHeating: 'District Heating',
    combustionBoiler: 'Combustion Boiler',
    electricBoiler: 'Electric Boiler',
    otherSource: 'Other Source',

    // Heat pump fields
    heatPumpType: 'Heat Pump Type',
    airToAir: 'Air-to-Air',
    airToWater: 'Air-to-Water',
    liquidToWater: 'Liquid-to-Water',
    heatingCapacity: 'Heating Capacity (kW)',
    liquidSource: 'Liquid Source',
    selectSource: 'Select source...',
    freshWater: 'Fresh Water',
    seaWater: 'Sea Water',
    sourceTemp: 'Source Temp (°C)',
    avgYearlyTemp: 'Avg Yearly Temp (°C)',
    maxTemperatureSupplied: 'Highest Source Temp (°C)',
    SourceTempeAmplitude: 'Source Temp Amplitude (°C)',
    energyWellDimensioning: 'Energy well use and dimensioning',
    energyWellHeatingOnlyUndersized:
      'Heating only (heat extraction), somewhat undersized energy well',
    energyWellHeatingOnlyGood:
      'Heating only (heat extraction), well dimensioned energy well',
    energyWellSomeCoolingUndersized:
      'Heating and some cooling, somewhat undersized energy well',
    energyWellSomeCoolingGood:
      'Heating and some cooling, well dimensioned energy well',
    energyWellBalancedUndersized:
      'Balanced heating and cooling, somewhat undersized energy well',
    energyWellBalancedGood:
      'Balanced heating and cooling, well dimensioned energy well',
    highestSourceTemperature: 'Highest supplied source temperature (°C)',
    waterSourceDepth: 'Heat extraction depth in sea/water',
    CO2EmissionFactor: 'CO₂ Emission Factor (g/kWh)',

    // District heating / Electric boiler / Other source fields
    maxCapacity: 'Max Capacity (kW)',
    energyPrice: 'Energy Price (per kWh)',
    temperatureFromSource: 'Temperature from Source (°C)',
    // Combustion boiler fields
    boilerType: 'Boiler Type',
    biomass: 'Biomass',
    fuelSource: 'Fuel Source',
    otherFuelSource: 'Other',
    fuelType: 'Fuel Type',
    flueGasCondensation: 'Flue Gas Condensation',
    fuelConfig: 'Fuel Configuration',
    fuelEnergyContent: 'Energy Content (kWh/L)',
    efficiencyLowLoad: 'Effect at 30% Load (kW)',
    effectFullLoad: 'Effect at Full Load (kW)',
    isCondensing: 'Condensing Boiler',
    operatingSchedule: 'Operating Schedule',
    startDate: 'Start (Day/Month)',
    endDate: 'End (Day/Month)',
    startOfDay: 'Start Hour',
    endOfDay: 'End Hour',
    seasonalMeanTemp: 'Seasonal Mean Temperature (°C)',

    // Solar thermal fields
    solarType: 'Solar Type',
    vacuumTube: 'Vacuum Tube',
    flatPlate: 'Flat Plate',
    tilt: 'Tilt (°)',
    area: 'Total Area (m²)',
    collectorArea: 'Collector Area (m²)',
    pipeLength: 'Pipe Length (m)',
    pipeUValue: 'Pipe U-Value (W/mK)',
    collectorSpecs: 'Collector Specifications',
    efficiencyNormal: 'Efficiency η₀',
    heatLossLinear: 'Heat Loss a₁ (W/m²K)',
    heatLossSecond: 'Heat Loss a₂ (W/m²K²)',
    opticalEfficiency: 'Optical Efficiency',
    heatLossCoeff: 'Heat Loss Coefficient',
    shadingWinter: 'Shading Factor Winter',
    shadingSummer: 'Shading Factor Summer',
    adjustmentFactor: 'Adjustment Factor',
    tankSpiralPlacementFactor: 'Tank Spiral Placement Factor',
    temperatureColdWater: 'Temperature Cold Water (°C)',
    temperatureInCollectionTank: 'Temperature in Collection Tank (°C)',
    percentageOfCollectorLoopOutside:
      'Percentage of Collector Loop Outside (%)',
    snowCoverageMonths: 'Snow Coverage Months',
    selectMonths: 'Select months...',
    insulationFactorForCollectionLoop: 'Insulation Factor for Collection Loop',
    selectInsulationFactorForCollectionLoop: 'Select insulation factor...',

    // District heating tables O.1, O.2, O.3
    heatExchangerInsulation: 'Heat Exchanger Insulation',
    selectHeatExchangerInsulation: 'Select insulation...',
    outletTemperatureRange: 'Outlet Temperature Range',
    selectOutletTempRange: 'Select range...',
    outletTempLow: '≤ 50 °C',
    outletTempMedium: '50–70 °C',
    outletTempHigh: '≥ 70 °C',
    bdhCoefficient: 'Heat Exchanger Loss',
    bdhValue: 'Loss Coefficient',
    supplyTemperatureRange: 'Supply Temperature Range',
    selectSupplyTempRange: 'Select range...',
    supplyTempHigh: '≥ 150 °C',
    supplyTempMedium: '105–150 °C',
    supplyTempLow: '≤ 105 °C',
    ddhCoefficient: 'Distribution Heat Loss',
    ddhValue: 'Loss Coefficient',
    coolingHeatExchangerInsulation: 'Cooling HX Insulation',
    selectCoolingHxInsulation: 'Select insulation...',
    coolingHeatExchangerUValue: 'Cooling Heat Exchanger',
    coolingHxUValue: 'U-Value (W/m²K)',

    // CHP tables Q.1, Q.2
    isCHP: 'Combined Heat and Power (CHP)',
    chpSettings: 'CHP Settings',
    chpFuelType: 'CHP Fuel Type',
    selectChpFuelType: 'Select fuel type...',
    chpSolidFuel: 'Solid Fuel',
    chpLiquidFuel: 'Liquid Fuel',
    chpGas: 'Gas',
    chpPerformanceLevel: 'Performance Level',
    selectChpPerformance: 'Select performance...',
    chpThermalEfficiency: 'Thermal Eff. (%)',
    chpElectricalEfficiency: 'Electrical Eff. (%)',
    chpTotalEfficiency: 'Total Eff. (%)',
    chpBoilerType: 'CHP Boiler Type',
    selectChpBoilerType: 'Select boiler type...',
    chpBiopellets: 'Bio-pellets',
    chpWoodChips: 'Wood Chips',
    gasOrOilBoiler: 'Gas or Oil Boiler',
    chpC7: 'C\u2087',
    chpC8: 'C\u2088',
    chpN: 'n',

    // Solar thermal pipe insulation table H.2
    pipeInsulationType: 'Pipe Insulation Type',
    selectPipeInsulation: 'Select insulation...',
  },

  // Excess Energy Module
  excessEnergy: {
    eligibleForHarvesting: 'Eligible for Energy Harvesting',
    eligibleForHarvestingHelp:
      'Mark this module as eligible for excess energy harvesting',

    // Duct dimensions
    ductDimensions: 'Duct Dimensions',
    ductShape: 'Duct Shape',
    diameter: 'Diameter (mm)',
    width: 'Width (mm)',
    height: 'Height (mm)',
    airspeed: 'Airspeed (m/s)',

    // Flow properties
    flowProperties: 'Flow Properties',
    temperature: 'Exhaust Temperature (C)',
    volumeFlow: 'Volume Flow',
    volumeFlowAir: 'Volume Flow (m3/h)',
    volumeFlowLiquid: 'Volume Flow (L/h)',

    // Delete
    deleteModule: 'Delete excess energy module',
    confirmDelete: 'Delete this excess energy module?',
    noModuleFound: 'No excess energy module found',
  },

  solarPv: {
    // Section headers
    soilingFactors: 'Soiling Factors (\u03C6_soil)',
    panelProperties: 'Panel Properties',
    lossesCoefficients: 'Losses & Coefficients',
    // Panel angle
    panelAngle: 'Panel Angle',
    tiltRange: 'Tilt range: {range}\u00B0',
    angleOutOfRange: 'Angle out of range',
    // Soiling
    selectLocation: 'Select location...',
    usingStandardTable: 'Using {standard} table for tilt range {range}\u00B0',
    // Panel properties
    selectCellType: 'Select cell type...',
    nominalEfficiency: 'Nominal Efficiency (0-1)',
    panelAzimuth: 'Panel Azimuth (0-360\u00B0)',
    shadingFactor: 'Shading Factor (0-1)',
    // Losses & coefficients
    mountingType: 'Mounting Type',
    selectMountingType: 'Select mounting type...',
    windExposure: 'Wind Exposure',
    selectWindExposure: 'Select wind exposure...',
    heatLossFactor: 'Heat Loss Factor (W/m\u00B2K)',
    inverterEfficiency: 'Inverter Efficiency (0-1)',
    degradationLoss: 'Degradation Loss (0-1)',
    inverterLoss: 'Inverter Loss (0-1)',
    transmissionProduct: 'Transmission Product (0-1)',
    temperatureCoefficient: 'Temperature Coefficient (%/\u00B0C)',
    incidentAngleModifier: 'Incident Angle Modifier (0-1)',
    // Delete
    deleteModule: 'Delete solar PV module',
    confirmDelete: 'Delete this solar PV module?',
  },

  // Battery edit page
  battery: {
    annualSimOnly:
      'Batteries are currently only included in annual simulations. They are not yet part of energy label calculations.',
    maxCapacity: 'Max Capacity (kWh)',
    maxChargePower: 'Max Charge Power (kW)',
    maxDischargePower: 'Max Discharge Power (kW)',
    chargeEfficiency: 'Charge Efficiency (0-1)',
    dischargeEfficiency: 'Discharge Efficiency (0-1)',
    minStateOfCharge: 'Min State of Charge (0-1)',
    maxStateOfCharge: 'Max State of Charge (0-1)',
    peakShavingThreshold: 'Peak Shaving Threshold (kW)',
    preChargeStateOfCharge: 'Pre-charge State of Charge (0-1)',
    maxCapacityRequired: 'Max capacity greater than 0 is required',
    maxChargePowerRequired: 'Max charge power greater than 0 is required',
    maxDischargePowerRequired: 'Max discharge power greater than 0 is required',
    deleteBattery: 'Delete battery',
    confirmDelete: 'Delete this battery?',
  },

  // HVAC system type options
  hvacSystemTypes: {
    balanced: 'Balanced',
    exhaust: 'Exhaust',
    supply: 'Supply',
  },

  // HVAC edit page
  hvac: {
    deleteUnit: 'Delete HVAC unit',
    confirmDelete: 'Delete this HVAC unit?',
    // List columns
    heatRecoveryShort: 'Heat Recovery',
    airVolumeShort: 'Air Volume',
    // DX Systems
    addDxSystem: 'Add DX System',
    confirmDeleteDxSystem: 'Delete this DX system?',
    // Heating batteries
    addHeatingBattery: 'Add Heating Battery',
    confirmDeleteHeatingBattery: 'Delete this heating battery?',
    // Cooling batteries
    addCoolingBattery: 'Add Cooling Battery',
    confirmDeleteCoolingBattery: 'Delete this cooling battery?',

    // Section headers
    namePlaceholder: 'HVAC unit name',
    dxSystemNamePlaceholder: 'DX system name',
    batteryNamePlaceholder: 'Battery name',
    flowSfpSettings: 'Flow & SFP Settings',
    temperatureMeasurements: 'Temperature Measurements',
    dxSystems: 'DX (Direct Expansion)',
    heatingBatteries: 'Heating Batteries',
    coolingBatteries: 'Cooling Batteries',
    naturalVentilation: 'Natural Ventilation',

    // SFP fields
    sfpCalculationMethod: 'SFP Calculation Method',
    sfpAsFunction: 'Specific fan power',
    yearBands: {
      pre1985: 'Before 1985',
      '1985to1995': '1985 to 1995',
      '1995to2005': '1995 to 2005',
      '2005to2013': '2005 to 2013',
      '2013to2020': '2013 to 2020',
      from2020: '2020 and newer',
    },
    suggestions: {
      yearHelp:
        'The year the ventilation unit was installed. It determines the typical motor type, SFP and heat recovery for its era.',
      hintLabel: 'Suggestion',
      calculatedLabel: 'Calculated',
      apply: 'Use',
      applyAll: 'Use ({count}) suggestions',
      noSuggestions: 'No suggestions',
      applyAllHelp: 'Apply every suggested value on this page.',
    },

    // Flow fields
    fanPower: 'Fan Power',
    coverage: 'Coverage',
    fanPowerIn: 'Fan Power In (kW)',
    fanPowerOut: 'Fan Power Out (kW)',
    // Supply air volume
    supplyAirVolume: 'Supply Air Volume',
    airVolumeOperating: 'Operating (m³/h)',
    airVolumeCapacity: 'Capacity (m³/h)',
    airVolumeNonOperating: 'Non-Operating (m³/h)',
    // Extraction air volume
    extractionAirVolume: 'Extraction Air Volume',
    airVolumeExtractionOperating: 'Operating (m³/h)',
    airVolumeExtractionCapacity: 'Capacity (m³/h)',
    airVolumeExtractionNonOperating: 'Non-Operating (m³/h)',

    // Heat recovery
    heatRecoveryType: 'Heat Recovery Type',
    efficiencyHR: 'Efficiency HR (%)',
    frostProtection: 'Frost Protection',
    frostProtectionTemperature: 'Frost Protection Temperature (°C)',

    // Temperature
    measuredSupplyTemp: 'Measured Supply Temp (°C)',
    measuredExhaustTemp: 'Measured Exhaust Temp (°C)',

    // DX System
    dxSystem: 'DX System',
    dxType: 'Type',

    // Battery fields
    heatingBattery: 'Heating Battery',
    coolingBattery: 'Cooling Battery',
    batteryType: 'Type',

    // Priority lists
    heatingPriorityList: 'Heating Priority',
    coolingPriorityList: 'Cooling Priority',
    priorityListHelp: 'Drag to reorder priority for calculations',
    noPriorityItems: 'No items configured',

    // zone linking
    linkedZones: 'Linked Zones',
    noLinkedZones: 'No zones linked',
    manageZones: 'Manage Zones',
    useZoneMap: 'Use zone map',
    linkZonesTitle: 'Link Thermal Zones',
    noZonesAvailable: 'No thermal zones available',
    zonesLinked: 'Zones linked successfully',
    noZoneWarning: 'This HVAC unit is not linked to a thermal zone.',
    noZoneListWarning: '{name} is not linked to a thermal zone.',
    sfp100Required: 'SFP @ 100% greater than 0 is required',
    airVolumeOperatingRequired:
      'Supply operating air volume greater than 0 is required',
    airVolumeCapacityRequired:
      'Supply capacity air volume greater than 0 is required',
    airVolumeExtractionOperatingRequired:
      'Extract operating air volume greater than 0 is required',
    dxEnergySourceRequired: 'Each DX system needs an energy source',
    dxSystemEfficiencyRequired:
      'Each DX system needs a system efficiency greater than 0',
    heatingBatteryPowerRequired:
      'Each heating battery needs a power greater than 0',
    heatingBatterySystemEfficiencyRequired:
      'Each heating battery needs a system efficiency greater than 0',
    heatingBatteryEnergySourceRequired:
      'Each heating battery needs an energy source',
    coolingBatteryPowerRequired:
      'Each cooling battery needs a power greater than 0',
    coolingBatterySystemEfficiencyRequired:
      'Each cooling battery needs a system efficiency greater than 0',
    coolingBatteryEnergySourceRequired:
      'Each cooling battery needs an energy source',

    supplementaryFactor: 'Supplementary Factor',

    // Motor type dropdown options
    motorType: 'Motor Type',
    motorTypes: {
      none: 'None',
      belt: 'Belt drive',
      axial: 'Axial fan',
      EC: 'EC motor',
      AC: 'AC motor',
      DC: 'DC motor',
      VSD: 'Variable Speed Drive (VSD)',
      other: 'Other',
    },

    // DX type dropdown options
    dxTypes: {
      cooling: 'Cooling only',
      heating: 'Heating only',
      both: 'Both heating and cooling',
    },

    // Flow type radio options
    flowTypes: {
      variable: 'Variable flow',
      constant: 'CAV, constant air volume',
      optimal: 'DCV, demand controlled',
    },

    // Battery types
    batteryTypes: {
      water: 'Water',
      electric: 'Electric',
      heatPump: 'Heat Pump',
      other: 'Other',
    },

    // Heat recovery types (keys match NS 3031:2025 Table B.6)
    heatRecoveryTypes: {
      rotaryWheel: 'Rotary wheel',
      crossFlow: 'Cross flow plate',
      plateCounterflow: 'Plate counterflow',
      runAroundCoil: 'Run around coil',
      heatPipe: 'Heat pipe',
      none: 'None',
    },
  },

  // Exterior wall edit page
  exteriorWall: {
    namePlaceholder: 'Wall name',
    direction: 'Direction',
    type: 'Wall Type',
    shadingFactor: 'Shading Factor',
    deleteWall: 'Delete wall',
    confirmDelete: 'Delete this wall?',
    directionRequired: 'Please select a wall direction',
    areaRequired: 'Wall area greater than 0 is required',
    uValueRequired: 'U-value greater than 0 is required',
    windowsExceedArea: 'Total window area exceeds the wall area.',
    doorsExceedArea: 'Total door area exceeds the wall area.',
    gatesExceedArea: 'Total gate area exceeds the wall area.',
    combinedExceedArea:
      'Combined area of windows, doors and gates exceeds the wall area.',
    windowsExceedAreaList: '{name}: total window area exceeds the wall area.',
    doorsExceedAreaList: '{name}: total door area exceeds the wall area.',
    gatesExceedAreaList: '{name}: total gate area exceeds the wall area.',
    combinedExceedAreaList:
      '{name}: combined area of windows, doors and gates exceeds the wall area.',
    options: {
      custom: 'Custom',
      standard: 'Standard',
      timberFrame36_150: 'Timber frame 36mm, 150mm insulation',
      timberFrame36_200: 'Timber frame 36mm, 200mm insulation',
      timberFrame36_250: 'Timber frame 36mm, 250mm insulation',
      timberFrame48_150: 'Timber frame 48mm, 150mm insulation',
      timberFrame48_200: 'Timber frame 48mm, 200mm insulation',
      timberFrame48_250: 'Timber frame 48mm, 250mm insulation',
      epsBlock310: 'EPS block 310mm (140mm concrete core)',
      epsBlock370: 'EPS block 370mm (140mm concrete core)',
      epsBlock430: 'EPS block 430mm (140mm concrete core)',
      iProfile200: 'I-profile steel frame, 200mm insulation',
      iProfile300: 'I-profile steel frame, 300mm insulation',
      iProfile400: 'I-profile steel frame, 400mm insulation',
      lwaBlock250: 'LWA block (Isoblokk) 250mm',
      lwaBlock300: 'LWA block (Isoblokk) 300mm',
      lwaBlock350: 'LWA block (Isoblokk) 350mm',
      slottedSteel200: 'Slotted steel, 200mm insulation',
      slottedSteel250: 'Slotted steel, 250mm insulation',
      slottedSteel300: 'Slotted steel, 300mm insulation',
      unslottedSteel200: 'Unslotted steel, 200mm insulation',
      unslottedSteel250: 'Unslotted steel, 250mm insulation',
      unslottedSteel300: 'Unslotted steel, 300mm insulation',
    },
  },

  // Building types (NS 3031 categories)
  buildingTypes: {
    label: {
      smallHouse: 'Small House',
      apartmentBlock: 'Apartment Block',
      kindergarten: 'Kindergarten',
      officeBuilding: 'Office',
      schoolBuilding: 'School',
      universityCollege: 'University/College',
      hospital: 'Hospital',
      nursingHome: 'Nursing Home',
      hotelBuilding: 'Hotel',
      sportsBuilding: 'Sports',
      commercialBuilding: 'Commercial',
      culturalBuilding: 'Cultural',
      lightIndustry: 'Light Industry/Warehouse',
    },
    description: {
      smallHouse:
        'Single-family house, semi-detached, terraced house, chain house, or other small residential buildings.',
      apartmentBlock:
        'Residential block with two or more floors, including large terraced houses with multiple floors.',
      kindergarten:
        'Buildings designed for kindergarten or early childhood education.',
      officeBuilding:
        'Simple offices, offices with ventilation and cooling, or exhibition/congress buildings.',
      schoolBuilding:
        'Teaching premises, including schools with swimming pools or sports halls.',
      universityCollege:
        'University or college buildings, laboratory buildings, or research facilities.',
      hospital:
        'Hospitals, hospital hotels, or laboratory buildings for medical use.',
      nursingHome: 'Nursing home buildings for long-term residential care.',
      hotelBuilding:
        'Hotels, restaurant buildings, or other buildings for accommodation.',
      sportsBuilding:
        'Sports halls, swimming pools, and other athletic facilities with shower areas.',
      commercialBuilding:
        'Shops, stores with cold/freezer rooms, or petrol stations.',
      culturalBuilding: 'Cinemas, museums, libraries, and religious buildings.',
      lightIndustry:
        'Industrial buildings, warehouses, fishery/agricultural buildings, energy supply facilities, or other workshops.',
    },
  },

  buildingSubcategories: {
    1: 'Detached house',
    2: 'Detached house with rental unit',
    3: 'Semi-detached house (horizontal)',
    4: 'Semi-detached house (vertical)',
    5: 'Four-unit house',
    6: 'Chained detached house',
    7: 'Terraced house',
    8: 'Holiday home',
    9: 'Other small house',
    10: 'Apartment',
    11: 'Other residential',
    12: 'Kindergarten building',
    13: 'Simple offices',
    14: 'Offices with ventilation and cooling',
    15: 'Exhibition/congress building',
    16: 'Other office building',
    17: 'Teaching premises',
    18: 'School with swimming facility',
    19: 'School with sports hall',
    20: 'Educational building',
    21: 'Laboratory building',
    22: 'Other research building',
    23: 'Hospital',
    24: 'Hospital hotel',
    25: 'Hospital laboratory building',
    26: 'Nursing home building',
    27: 'Hotel building',
    28: 'Accommodation building',
    29: 'Restaurant building',
    30: 'Other hotel building',
    31: 'Sports hall',
    32: 'Sports hall with shower facility',
    33: 'Sports hall with swimming facility',
    34: 'Shop',
    35: 'Shop with cold storage',
    36: 'Petrol station',
    37: 'Cultural building',
    38: 'Cinema',
    39: 'Museum/library building',
    40: 'Building for religious activities',
    41: 'Industrial building',
    42: 'Energy supply building',
    43: 'Warehouse building',
    44: 'Fishery/agricultural building',
    45: 'Other industry',
  },

  thermalZones: {
    schedule: {
      heatingSchedule: 'Heating Schedule',
      coolingSchedule: 'Cooling Schedule',
      addHeatingSchedule: 'Add heating schedule',
      addCoolingSchedule: 'Add cooling schedule',
    },
    yearPeriods: {
      TEK50: 'Before 1969',
      TEK70: '1970 - 1979',
      TEK80: '1980 - 1986',
      TEK87: '1987 - 1996',
      TEK97: '1997 - 2006',
      TEK07: '2007 - 2009',
      TEK10: '2010 - 2016',
      TEK17: '2017 onwards',
    },
    hasFloorToGround: 'Zone has floor to ground',
    hasRoof: 'Zone has roof',
    isBasement: 'Zone is a basement',
    floor: {
      type: 'Floor to Ground Type',
      area: 'Floor to ground (m²)',
      options: {
        custom: 'Custom',
        standard: 'Standard',
        tiles: 'Tiles on concrete',
        parquetChipBoard: 'Parquet on chipboard',
        carpetConcrete: 'Carpet on concrete',
      },
    },
    roof: {
      type: 'Roof Type',
      optionsTitle: 'Roof Construction',
      angle: 'Roof Angle',
      hasWindows: 'Roof has windows',
      windowArea: 'Window Area (m²)',
      glassType: 'Glass Type',
      areaRequired: 'Roof area greater than 0 is required',
      pitchTypes: {
        flat: 'Flat roof',
        lowSlope: 'Low slope',
        gentlePitch: 'Gentle pitch',
        standardPitch: 'Standard pitch',
        steepPitch: 'Steep pitch',
        verySteepPitch: 'Very steep pitch',
      },
      options: {
        custom: 'Custom',
        standard: 'Standard',
        compactConcrete200: 'Compact roof, concrete, 200mm insulation',
        compactConcrete300: 'Compact roof, concrete, 300mm insulation',
        compactConcrete400: 'Compact roof, concrete, 400mm insulation',
        compactSteel200: 'Compact roof, steel, 200mm insulation',
        compactSteel300: 'Compact roof, steel, 300mm insulation',
        compactSteel400: 'Compact roof, steel, 400mm insulation',
        pitched48mm200: 'Pitched roof, 48mm rafters, 200mm insulation',
        pitched48mm300: 'Pitched roof, 48mm rafters, 300mm insulation',
        pitched48mm400: 'Pitched roof, 48mm rafters, 400mm insulation',
        pitchedIBeam200: 'Pitched roof, I-beam rafters, 200mm insulation',
        pitchedIBeam300: 'Pitched roof, I-beam rafters, 300mm insulation',
        pitchedIBeam450: 'Pitched roof, I-beam rafters, 450mm insulation',
        coldAttic200: 'Cold attic, 200mm insulation',
        coldAttic300: 'Cold attic, 300mm insulation',
        coldAttic450: 'Cold attic, 450mm insulation',
      },
    },
    basement: {
      dimensions: 'Dimensions',
      area: 'Floor area against ground (m²)',
      wallPerimeter: 'Exposed wall perimeter (m)',
      fillHeight: 'Depth below ground (m)',
      wallThickness: 'Wall thickness (m)',
      wallConstruction: 'Basement wall',
      wallConstructionType: 'Wall type',
      wallUValue: 'Wall U-value (W/m²K)',
      floorConstruction: 'Basement floor',
      floorConstructionType: 'Floor type',
      floorUValue: 'Floor U-value (W/m²K)',
      intro:
        'Remember to reduce the floor to ground of the zone above by the basement area.',
      floorConstructionOptions: {
        none: 'None',
        standard: 'Standard',
        concrete100: 'Concrete on ground with 100mm insulation',
        concrete150: 'Concrete on ground with 150mm insulation',
        concrete200: 'Concrete on ground with 200mm insulation',
        concrete250: 'Concrete on ground with 250mm insulation',
        concrete300: 'Concrete on ground with 300mm insulation',
        concrete350: 'Concrete on ground with 350mm insulation',
        concrete400: 'Concrete on ground with 400mm insulation',
        wood200: 'Wooden joist with 200mm insulation',
        wood250: 'Wooden joist with 250mm insulation',
        wood300: 'Wooden joist with 300mm insulation',
      },
    },
    groundConditions: {
      type: 'Ground Type',
      thermalConductivity: 'Thermal Conductivity λ (W/(m·K))',
      heatCapacity: 'Heat Capacity Cv (Wh/(m³·K))',
      penetrationDepth: 'Penetration Depth δ (m)',
      options: {
        clay: 'Clay/silt',
        sand: 'Sand/gravel',
        rock: 'Rock',
      },
    },
    thermalMass: {
      buildingHeatCapacity: 'Zone Heat Capacity',
      heatCapacityCategory: 'Heat Capacity Category',
      normalizedHeatCapacity: 'Normalized Heat Capacity (Wh/m²K)',
      interiorWalls: 'Interior Walls',
      cladding: 'Cladding',
      heatCapacity: 'Heat Capacity (Wh/m²K)',
      furniture: 'Furniture',
      furnitureGrade: 'Furniture Grade',
      massFactor: 'Mass Factor (Wh/m²K)',
      heatCapacityCategoryRequired: 'Please select a heat capacity category',
      claddingRequired: 'Please select a cladding type',
      furnitureGradeRequired: 'Please select a furniture grade',
      labelFirstOption: 'Very light zone',
      labelSecondOption: 'Light zone',
      labelThirdOption: 'Medium heavy zone',
      labelFourthOption: 'Heavy zone',
      labelFifthOption: 'Very heavy zone',
      shortDescriptionFirstOption:
        'Light joist floor, thin floor covering, light walls',
      shortDescriptionSecondOption:
        'Acoustic ceiling, carpet over concrete, light walls',
      shortDescriptionThirdOption:
        'Low exposed concrete (20%), parquet over concrete',
      shortDescriptionFourthOption: 'Mostly exposed concrete (70%)',
      shortDescriptionFifthOption:
        'Exposed concrete in ceiling, walls and floor',
      claddingOptions: {
        custom: 'Custom',
        standard: 'Standard',
        concrete100: '100mm (or more) concrete',
        concrete80: '80mm concrete',
        aeratedConcrete: 'Aerated concrete',
        gypsum13: 'Gypsum 13mm',
        lightBrick: 'Light brick',
        lwa: 'Lightweight aggregate',
        solidWood40: 'Solid wood (thickness over 40mm)',
        woodPanel15: 'Wood panel 15mm',
        heavyBrick: 'Heavy (compact) brick',
      },
      furnitureOptions: {
        none: 'None',
        standard: 'Standard',
        light: 'Light',
        medium: 'Medium',
        heavy: 'Heavy',
      },
    },
    airLeakage: {
      tekStandard: 'TEK Standard',
      n50: 'Leakage number n50 (h⁻¹)',
      tekOptions: {
        TEK50: 'Building regulations 1950–1969',
        TEK70: 'Building regulations 1970–1979',
        TEK80: 'Building regulations 1980–1986',
        TEK87: 'TEK 87 (1987-1996)',
        TEK97: 'TEK 97 (1997-2006)',
        TEK07: 'TEK 07 (2007-2009)',
        TEK10: 'TEK 10 (2010-2016)',
        TEK17: 'TEK 17 (2017+)',
      },
    },
    windows: {
      deleteWindow: 'Delete window',
      confirmDelete: 'Delete this window?',
      gValue: 'g-Value',
      frameFraction: 'Frame fraction',
      shadingFactor: 'Shading factor (0-1)',
      externalBlinds: 'External blinds (gt)',
      externalBlindsGt: 'External blinds gt',
      externalScreen: 'External screen (gt)',
      externalScreenGt: 'External screen gt',
      internalShading: 'Internal shading (gt)',
      internalShadingGt: 'Internal shading gt',
      windowTypes: {
        none: 'None',
        custom: 'Custom',
        singleGlass: 'Single glass',
        twoLayerGlass: '2-layer coupled glass',
        twoLayerEnergyGlass: '2-layer energy glass',
        twoLayerSolarReflective: '2-layer solar reflective glass',
        threeLayerEnergyGlass: '3-layer energy glass',
      },
    },
    doors: {
      deleteDoor: 'Delete door',
      confirmDelete: 'Delete this door?',
      type: 'Door Type',
      doorTypes: {
        custom: 'Custom',
        standard: 'Standard',
        insulated: 'Insulated',
        normal: 'Normal',
        wellInsulated: 'Well insulated',
        uninsulated: 'Uninsulated',
      },
    },
    gates: {
      deleteGate: 'Delete gate',
      confirmDelete: 'Delete this gate?',
      type: 'Gate Type',
      gateTypes: {
        custom: 'Custom',
        standard: 'Standard',
        insulated: 'Insulated',
        sectional: 'Sectional',
        roller: 'Roller',
        wellInsulated: 'Well insulated',
      },
    },
    internalLoads: {
      internalLoadsNotice:
        'These values are not used when creating an energy label. Only presence control for lighting is included.',
      hotWater: 'Hot Water',
      equipment: 'Technical Equipment',
      occupancy: 'People',
      weekday: 'Weekday (260 days)',
      weekend: 'Weekend (105 days)',
      resetToStandards: 'Reset to',
      hourlyProfile: 'Hourly Profile',
      editHourlyValues: 'Edit Hourly Values',
      peak: 'Peak',
      avg: 'Avg',
      daily: 'Daily',
      annual: 'Annual',
      appliances: 'Appliances',
      appliancesDescription:
        'Reduce equipment electricity demand by connecting appliances to hot water.',
      dishwasherLabel: 'Dishwasher (hot water)',
      washingMachineLabel: 'Washing machine (hot water)',
      tumbleDryerLabel: 'Tumble dryer (hot water)',
      applianceShareLabel: 'Thermal share [%]',
      heatGainShareLabel: 'Heat gain to zone [1-100%]',
      lampTypes: 'Lamp types',
      lampType: 'Lamp type',
      selectLampType: 'Select lamp type',
      ballast: 'Ballast',
      electronic: 'Electronic',
      electromechanical: 'Electromechanical',
      sharePercent: 'Share (%)',
      efficiencyFactorFL: 'Efficiency factor (FL)',
      lampTypeNames: {
        ledNew: 'LED from 2016 and newer',
        ledOld: 'LED from 2010–2015',
        fluorescentT5: 'Fluorescent T5',
        fluorescentT8: 'Fluorescent T8',
        metalHalide: 'Metal halide lamp',
        highPressureSodium: 'High pressure sodium lamp',
        compactFluorescent: 'Compact fluorescent lamp',
        halogen: 'Halogen lamp',
        incandescent: 'Incandescent lamp',
      },
      addLampType: 'Add lamp type',
      deleteLampType: 'Delete this lamp type?',
    },
    heatingEmissionSystem: 'Heating Emission System',
    heatingEmissionSystems: {
      radiator_thermostat_80_60:
        'Radiator/convector with thermostatic valve or local room controller',
      radiator_thermostat_60_40:
        'Radiator/convector with thermostatic valve or local room controller',
      radiator_thermostat_40_35:
        'Radiator/convector with thermostatic valve or local room controller',
      radiator_sd_80_60: 'Radiator/convector connected to BMS',
      radiator_sd_60_40: 'Radiator/convector connected to BMS',
      radiator_sd_40_35: 'Radiator/convector connected to BMS',
      fan_coil_heat_pump:
        'Fan coil / heat pump / active chilled beam / heating via ventilation air',
      floor_heating_thermostat:
        'Underfloor heating with thermostatic valve or local room controller',
      floor_heating_sd: 'Underfloor heating connected to BMS',
      panel_heater_pre_2010: 'Panel heater \u2013 pre 2010',
      panel_heater_post_2010: 'Panel heater \u2013 post 2010',
    },
    coolingEmissionSystem: 'Cooling Emission System',
    coolingEmissionSystems: {
      chilled_beam_thermostat:
        'Passive chilled beam with thermostatic valve or local room controller',
      chilled_beam_sd: 'Passive chilled beam connected to BMS',
      fan_coil_cool_pump:
        'Active chilled beam / fan coil with local room controller',
      fan_coil_cool_pump_sd: 'Active chilled beam / fan coil connected to BMS',
      floor_cooling_thermostat: 'Floor cooling with local room controller',
      floor_cooling_sd: 'Floor cooling connected to BMS',
      chilled_ceiling_thermostat: 'Chilled ceiling with local room controller',
      chilled_ceiling_sd: 'Chilled ceiling connected to BMS',
    },
    effectiveSetpoint: 'Effective setpoint',
  },

  // Thermal Zone Links
  thermalZoneLinks: {
    linkZones: 'Link Zones',
    resetLayout: 'Reset layout',
    resetLayoutConfirm:
      'Reset the map layout? All nodes return to the suggested placement.',
    confirmLink: 'Confirm Link',
    linkDialogTitle: 'Link Thermal Zones',
    linkingZones: 'Linking Zones',
    sourceZone: 'Source Zone',
    targetZones: 'Target Zones',
    areaUnit: 'm²',
    uValueUnit: 'W/m²K',
    selectSourceFirst: 'Select source zone first, then target zones',
    minTwoZones: 'Select at least 2 zones to link',
    linkSuccess: 'Zones linked successfully',
    externalZone: 'External Zone',
    zoneConnections: 'Zone Connection',
    addConnection: 'Add connection',
    noConnections: 'No zone connections',
    connectionDeleted: 'Connection removed',
    deleteConnectionError: 'Could not delete connection',
    connectionAdded: 'Connection added',
    connectionUpdated: 'Connection updated',
    editConnection: 'Edit connection',
    deleteConnection: 'Delete connection',
    confirmDeleteConnection: 'Remove zone connection?',
    connectToExisting: 'Existing zone',
    connectToExternal: 'External zone',
    chooseConnectionType: 'Connect to',
    zoneMap: 'Zone map',
    zoneMapEmpty: 'No zone connections',
    notLinked: 'Not linked',
    autoLinked: 'Automatically linked',
    linkModeHintSource: 'Click the source zone',
    linkModeHintTarget: 'Click the target zone, or choose external zone',
    deleteModeHint: 'Click the zone you want to delete connections from',
    ventilation: 'Ventilation',
    zoneMapSubtitle: 'Move the zones to adjust the layout.',
    noVentilationUnits: 'No ventilation units available',
    noHeatingCoolingModules: 'No heating or cooling available',
    linkAny: 'Link',
    linkModeHintSourceAny: 'Click the source',
    linkModeHintTargetAny: 'Click the target, or choose external zone',
    deleteModeHintAny:
      'Click the zone or ventilation unit you want to delete connections from',
  },

  analytics: {
    energyToggle: 'Energy',
    analyticsToggle: 'Analytics',
    compareToggle: 'Compare',
    runSimulation: 'Run Simulation',
    runCoverNeedSimulation: 'Run Minimum Requirement Simulation',
    coverNeedTooltip:
      'Runs the simulation with fictitious minimum requirement systems, covering the full energy need where systems are missing or fall short',
    coverNeedAssumptionsTitle: 'Minimum requirement simulation assumptions',
    simulating: 'Simulating...',
    exportToExcel: 'Export to Excel',
    sendToEnova: 'Send to Enova',
    resendToEnova: 'Resend to Enova',
    downloadEnovaPdf: 'Download Enova PDF',
    sentToEnovaSuccess: 'Sent to Enova successfully',
    sendToEnovaError: 'Failed to send to Enova',
    simulationStartedRefresh:
      'Calculations have started, refresh or go back to this page after 10 seconds',
    simulationStartError: 'Failed to start the simulation',
    runRequiresZoneSelection:
      'Select at least one zone before running the simulation.',
    solarNotIncludedInZones:
      'Solar production is calculated at building level and is not included in zone results and comparisons.',
    resultsCalculatedAt: 'Calculated {datetime}',
    resultsCalculatedWithStandard: 'Calculated {datetime} per {standard}',
    enovaMissingTiltakDescription:
      'Fill in the description under Actions in Energy Label.',
    enovaNoIncludedActions:
      "Enable at least one action to include, or uncheck 'Include measures' and provide a description.",
    enovaMissingActionDescriptions: 'Fill in descriptions for: {actions}',
    enovaMissingCredentials:
      'Fill in {fields} in your user profile before sending to Enova.',
    enovaNoMatrikkelSelected:
      'Select at least one bruksenhet in the Matrikkeldata section before sending to Enova.',
    enovaZonesNotSimulated:
      'Run the energy label simulation for the selected zones before sending to Enova.',
    enovaNonResidentialMultipleUnits:
      'Only one unit can be selected for non-residential sections.',
    confirmSendToEnovaMessage: 'Are you sure you want to send to Enova?',
    enovaSendWithMessagesWarning:
      'The simulation has {count} messages. Check them before you send to Enova.',
    simulationProgress: 'Running building energy simulation...',
    simulationMessages: 'Simulation Messages',
    energyCalcWithStandard:
      'Energy calculations following {standard} standards.',
    energyCalc: 'Energy calculations',
    noResults:
      'No simulation results available. Run a simulation to see energy data.',
    analyticsPlaceholder: 'Analytics charts will be displayed here.',
    // Chart titles & descriptions
    monthlyEnergyBudget: 'Monthly Energy Budget (Demand)',
    deliveredEnergyTitle: 'Delivered Energy Sources',
    deliveredEnergyDesc:
      'Total delivered energy represents all energy sources supplied to the building.',
    energyComparisonTitle: 'Energy Comparison (Net / Loss / Gross)',
    energyComparisonDesc:
      'Compares net energy demand, distribution losses, and gross energy for each category. Gross = Net + Loss.',
    // Chart labels
    showTemperature: 'Show Temperature Line',
    energySourcesChartLabel: 'Energy Sources',
    totalDeliveredEnergy: 'Total Delivered Energy',
    visible: 'Visible',
    noData: 'No data',
    nettEnergy: 'Net Energy',
    grossEnergy: 'Gross Energy',
    distributionLossLabel: 'Distribution and Accumulation Loss',
    noMonthlyData:
      'Monthly breakdown data is not available for this simulation.',
    monthlyChartHelp:
      'Each stacked bar represents the monthly net energy demand broken down by category. Heating categories are shown in warm colors, cooling in blue shades, and electrical loads in green/yellow/grey.',
    netEnergyDemand: 'Net Energy Demand',
    distributionLosses: 'Distribution and Accumulation Losses',
    grossEnergyDemand: 'Gross Energy Demand',
    energySourcesTitle: 'Energy Sources (Delivered Energy)',
    deliveredExportedEnergyTitle: 'Delivered and exported energy',
    climaCorrectedEnergyTitle:
      'Climate corrected delivered and exported energy',
    netEnergyTitle: 'Net Energy (Netto energibehov)',
    distributionLossTitle:
      'Distribution Loss (Distribusjon og akkumuleringstap)',
    grossEnergyTitle: 'Gross Energy (Brutto energibehov)',
    category: 'Category',
    value: 'Value',
    unit: 'Unit',
    co2: 'CO₂ kg',
    energyType: 'Energy type',
    electricityCategory: '1. Electricity',
    fossilCategory: '2-3. Fossil fuels',
    renewableCategory: '4. Renewable heating',
    districtCategory: '5. District energy',
    otherCategory: '6-8. Other sources',
    totalCategory: 'Total',
    elSpecific: 'El. specific items',
    elHeating: 'El. for space heating',
    elCooling: 'El. for cooling system',
    elDHW: 'El. for domestic hot water',
    elProducedUsed: 'El. production for own use',
    elProducedExported: 'El. production exported',
    oil: 'Oil',
    gas: 'Gas',
    solidFuel: 'Solid fuel',
    solidBiofuel: 'Solid biofuel',
    liquidBiofuel: 'Liquid biofuel',
    gaseousBiofuel: 'Gaseous biofuel',
    districtHeating: 'District heating',
    districtCooling: 'District cooling',
    other: 'Other',
    sumDelivered: 'Total',
    spaceHeating: 'Space heating',
    ventilationHeating: 'Ventilation heating',
    hotWater: 'Hot water',
    spaceCooling: 'Space cooling',
    ventilationCooling: 'Ventilation cooling',
    fans: 'Fans',
    pumps: 'Pumps',
    lighting: 'Lighting',
    technicalEquipment: 'Technical equipment',
    total: 'Total',
    // Heat loss
    heatLossTitle: 'Heat Loss',
    heatLossDesc:
      'Breakdown of building heat loss by component. Values in kWh.',
    hlWalls: 'Walls',
    hlRoof: 'Roof',
    hlFloor: 'Floor to ground',
    hlWindows: 'Windows',
    hlDoors: 'Doors',
    hlThermalBridge: 'Thermal Bridges',
    hlInfiltration: 'Infiltration',
    hlVentilation: 'Ventilation',
    hlTotal: 'Total',
    // Heat loss budget (Table 3 NS 3031:2025)
    hlBudgetTitle: 'Heat loss budget',
    hlBudgetCategory: 'Heat loss category',
    hlBudgetValue: 'Heat loss coefficient',
    hlWindowsDoors: 'Windows and doors',
    hlTransmissionSum: 'Heat loss coefficient, transmission, sum 1–5',
    hlClimateScreenSum: 'Heat loss coefficient, climate screen, sum 1–6',
    hlTotalSum: 'Heat loss coefficient, total, sum 1–7',
    // Primary energy
    peDeliveredEnergy: 'Delivered energy (electricity)',
    peDeliveredFuel: 'Delivered fuel',
    peDistrictHeating: 'District heating',
    peDistrictCooling: 'District cooling',
    peOtherDelivered: 'Other delivered energy',
    peElExport: 'El. production for export',
    energyLabelTitle: 'Energy Label',
    supplementaryEnergyTitle: 'Supplementary Energy',
    primaryEnergyFactorsTitle: 'Primary Energy Weighting Factors',
    carCharging: 'Car charging',
    otherEnergyNeeded: 'Other energy needed',
    energyNeedForOtherBuildings: 'Energy need for other buildings',
    peFossilOil: 'Fossil oil',
    peFossilGas: 'Fossil gas',
    peFossilSolid: 'Fossil solid fuel',
    peBioOil: 'Bio oil',
    peBioGas: 'Bio gas',
    peBioSolid: 'Bio solid fuel',
    ventilationSpecsTitle: 'Ventilation & Fan Specifications',
    specificFanPower: 'Specific fan power (SFP)',
    specificAirFlowIn: 'Specific supply air flow in operating hours',
    specificAirFlowOut: 'Specific supply air flow outside operating hours',
    specificExhaustAirFlowIn: 'Specific exhaust air flow in operating hours',
    specificExhaustAirFlowOut:
      'Specific exhaust air flow outside operating hours',
    hcsHeatingTitle: 'Heating systems',
    hcsCoolingTitle: 'Cooling systems',
    hcsType: 'Type',
    hcsHeatingFactor: 'Efficiency heat factor',
    hcsCoolingFactor: 'Efficiency cooling factor',
    hcsEnergyCoverage: 'Energy coverage rate',
    hcsInndata: 'Input data',
    hcsInstalledHeatingEffect:
      'Specific installed effect for space and ventilation heating',
    hcsInstalledCoolingEffect:
      'Specific installed effect for space and ventilation cooling',
    hcsSetpointInOperation: 'Equivalent setpoint temperature in operation',
    hcsSetpointOutOfOperation:
      'Equivalent setpoint temperature out of operation',
    wpeTitle: 'Climate corrected weighted delivered energy',
    wpeColSource: 'Energy source',
    wpeColDelivered: 'Climate corrected delivered and exported energy',
    wpeColFactor: 'Weighting factor',
    wpeColWeighted: 'Climate corrected weighted delivered energy',
    wpeTotal: 'Total, sum 1-6',
    wpeElectricity: 'Delivered electricity',
    wpeSolidBio: 'Delivered solid biofuel',
    wpeLiquidBio: 'Delivered liquid biofuel',
    wpeGasBio: 'Delivered gaseous biofuel',
    wpeSolidFossil: 'Delivered solid fossil fuel',
    wpeLiquidFossil: 'Delivered liquid fossil fuel',
    wpeGasFossil: 'Delivered gaseous fossil fuel',
    wpeDistrictHeating: 'Delivered district heating',
    wpeDistrictCooling: 'Delivered district cooling',
    wpeOther: 'Other delivered energy carriers',
    wpeElExport: 'Electricity production for export (deduction)',
    ekBasisHeader: 'Basis for energy character',
    ekValueHeader: 'Value',
    ekSpecificWeighted: 'Specific climate corrected weighted delivered energy',
    meTitle: 'Measured energy use (kWh/year)',
    meColSource: 'Energy carrier',
    meColYearGroup: 'Year',
    meColAverage: 'Average',
    meTotalDelivered: 'Total delivered energy',
    meTotalNetDelivered: 'Total net delivered energy (minus any export)',
    icTitle: 'Indoor air temperature',
    icSubtitle: 'Monthly average based on hourly values',
    icZoneLabel: 'Zone',
    icSingleZoneLabel: 'Indoor air temperature',
    dhcLocalHeatingElectricity: 'Local heating — electricity',
    dhcLocalHeatingFossilOil: 'Local heating — fossil oil',
    dhcLocalHeatingFossilGas: 'Local heating — fossil gas',
    dhcLocalHeatingFossilSolid: 'Local heating — fossil solid fuel',
    dhcLocalHeatingBioOil: 'Local heating — bio oil',
    dhcLocalHeatingBioGas: 'Local heating — bio gas',
    dhcLocalHeatingBioSolid: 'Local heating — bio solid fuel',
    dhcLocalDistrictHeating: 'Local district heating',
    dhcLocalHeatPump: 'Local heat pump',
    dhcLocalHeatingOther: 'Local heating — other sources',
    dhcCentralHeatPump: 'Central heat pump',
    dhcCentralHeatingElectricity: 'Central heating — electricity',
    dhcCentralHeatingFossilOil: 'Central heating — fossil oil',
    dhcCentralHeatingFossilGas: 'Central heating — fossil gas',
    dhcCentralHeatingFossilSolid: 'Central heating — fossil solid fuel',
    dhcCentralHeatingBioOil: 'Central heating — bio oil',
    dhcCentralHeatingBioGas: 'Central heating — bio gas',
    dhcCentralHeatingBioSolid: 'Central heating — bio solid fuel',
    dhcCentralDistrictHeating: 'Central district heating',
    dhcCentralHeatingOther: 'Central heating — other sources',
    dhcCentralSolarThermal: 'Central solar thermal',
    dhcCentralCogeneration: 'Central cogeneration',
    dhcLocalCooling: 'Local cooling',
    dhcCentralCoolingAirWater: 'Central cooling — air/water',
    dhcCentralCoolingLiquidWater: 'Central cooling — liquid water',
    dhcCentralFreeCooling: 'Central free cooling',
    dhcCentralDistrictCooling: 'Central district cooling',
    // Internal loads
    internalLoadsTitle: 'Internal loads',
    internalLoadEnergyTitle: 'Internal Loads - Energy Need',
    internalLoadHeatTitle: 'Internal Loads - Heat Gains',
    ilLighting: 'Lighting',
    ilEquipment: 'Technical equipment',
    ilHotWater: 'Hot water',
    ilPeople: 'People',
    internalLoadsChartTitle: 'Internal Loads (Energy Need vs Heat Gain)',
    internalLoadsChartDesc:
      'Compares energy need and heat gain contributions from internal load sources.',
    ilEnergyNeed: 'Energy need',
    ilSpecificEnergyNeed: 'Specific energy need',
    ilSpecificHeatGain: 'Specific heat gain',
    ilHeatGain: 'Heat Gain',
    // Month names
    month: 'Month',
    monthJan: 'Jan',
    monthFeb: 'Feb',
    monthMar: 'Mar',
    monthApr: 'Apr',
    monthMay: 'May',
    monthJun: 'Jun',
    monthJul: 'Jul',
    monthAug: 'Aug',
    monthSep: 'Sep',
    monthOct: 'Oct',
    monthNov: 'Nov',
    monthDec: 'Dec',
    // Information table (general + climate)
    bdTitle: 'Information',
    bdParameter: 'General',
    bdInputValue: 'Input value',
    bdBuildingCategory: 'Building Category',
    bdBuildingType: 'Building Type',
    bdYearOfConstruction: 'Year of Construction',
    bdTekStandard: 'TEK Standard',
    bdCalculationDate: 'Calculation Date',
    bdClimateInformation: 'Climate',
    bdClimateStationSource: 'Climate Station Source',
    bdClimateStation: 'Climate Station',
    bdClimateCorrectionFactor: 'Climate Correction Factor',
    bdYearlyAverageTemperature: 'Yearly Average Temperature',
    efTitle: 'Energy flow',
    efColCategory: 'Energy category',
    efColNet: 'Net energy need',
    efColDist: 'Distribution and storage losses',
    efColGross: 'Gross energy need',
    efColSpf: 'SPF',
    efColDelivered: 'Delivered energy',
    efTotal: 'Total',
    // Energy carrier weighting factors (T.2 NS 3031:2025)
    wfTitle: 'Energy carrier weighting factors',
    wfType: 'Type',
    wfWeightFactor: 'Weighting factor',
    wfClimateFactor: 'Climate gas factor (CO²-equivalent)',
    wfEnergyPrice: 'Energy price',
    wfOtherWeighting: 'Other weighting',
    wfElectricity: 'Delivered electricity',
    wfBioSolid: 'Solid biofuel',
    wfBioLiquid: 'Liquid biofuel',
    wfBioGas: 'Gaseous biofuel',
    wfFossilSolid: 'Solid fossil fuel',
    wfFossilLiquid: 'Liquid fossil fuel',
    wfFossilGas: 'Gaseous fossil fuel',
    wfDistrictHeating: 'District heating',
    wfDistrictCooling: 'District cooling',
    wfOther: 'Other',
    wfExportedElectricity: 'Exported electricity',
    // Distribution systems
    dsTitle: 'Distribution systems',
    dsCategory: 'Parameter',
    dsValue: 'Input value',
    dsRoomHeating: 'Room heating',
    dsVentilationHeating: 'Ventilation heating',
    dsDhwCirculation: 'Hot water circulation',
    dsRoomCooling: 'Room cooling',
    dsVentilationCooling: 'Ventilation cooling',
    dsPipeLength: 'Pipe length per heated floor area',
    dsPumpPower: 'Specific pump power',
    // Electricity (Table T.2 NS 3031:2025)
    elTitle: 'Electricity',
    elCategory: 'Parameter',
    elValue: 'Input value',
    elSolarEfficiency: 'Annual efficiency of solar power system',
    elSolarInstalled: 'Installed effect of solar power plant (Ppeak)',
    elSolarCoverage: 'Solar coverage of specific electric energy need',
    elWindCoverage: 'Wind coverage of specific electric energy need',
    elChpEfficiency:
      'Annual average efficiency for cogeneration electricity production',
    elChpCoverage: 'Cogeneration coverage of specific electric energy need',
  },

  energyLabelConfig: {
    // air leakage
    airLeakageTitle: 'Air leakage',
    airLeakageMeasured: 'Air leakage measured',
    airLeakageMeasuredDate: 'Measurement date',
    airLeakageGradeAHint:
      'Grade A requires a measured air leakage with a measurement date. Without it the label is capped at B.',
    // actions
    actionsTitle: 'Actions',
    includeMeasures: 'Include measures under evaluation',
    includeActivatedActions: 'Include activated actions',
    loadingActions: 'Loading actions…',
    actionNameVentilation: 'Ventilation action',
    actionNameHeating: 'Heating and cooling action',
    actionNameLighting: 'Lighting action',
    actionNameSolar: 'Solar cells action',
    actionNameControl: 'Control & Regulation Action',
    actionsDescription:
      'If no measures are included, describe the reason instead (e.g., new construction).',
    includedMeasuresInfo:
      'The list of included measures contains an assessment of costs and consequences regarding moisture and indoor climate for each energy measure. The list can be used as a basis for planning a program for maintenance and upgrading of the building.',
    description: 'Description',
    // measured values
    measuredValuesTitle: 'Measured values',
    measuredValuesCheckbox: 'Measured values',
    measuredValuesDescription:
      'Energy consumption measurements by source type for the last 3 years.',
    energySourcePlaceholder: 'Energy source (kWh)',
    addMeasurement: 'Add measurement',
    confirmDeleteMeasurement: 'Delete this measurement row?',
    // energy source options
    deliveredElectricity: 'Delivered electricity (kWh)',
    exportedElectricity: 'Exported electricity (kWh)',
    fossilOil: 'Fossil oil (kWh)',
    fossilGas: 'Fossil gas (kWh)',
    fossilSolidFuel: 'Fossil solid fuel (kWh)',
    bioOil: 'Bio oil (kWh)',
    bioGas: 'Bio gas (kWh)',
    bioSolidFuel: 'Bio solid fuel (kWh)',
    districtHeating: 'District heating (kWh)',
    districtCooling: 'District cooling (kWh)',
    other: 'Other (kWh)',
    // matrikkel data accordion
    matrikkelDataTitle: 'Cadastral Data',
    noUnitsFound: 'No units found',
    street: 'Address',
    addressCode: 'Address Code',
    postcode: 'Postal Code',
    city: 'City',
    municipality: 'Municipality',
    municipalityNumber: 'Municipality Number',
    gnrBnr: 'Gnr/Bnr',
    leaseholdNumber: 'Leasehold Number',
    buildingNumber: 'Building Number',
    buildingType: 'Building Type',
    buildingStatus: 'Building Status',
    industryType: 'Industry Type',
    unitNumber: 'Unit Number',
    unitId: 'Unit ID',
    unitType: 'Unit Type',
    sectionNumber: 'Section Number',
    sectionType: 'Section Type',
  },

  zoneSelection: {
    title: 'Zones to simulate',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    selected: '{count} of {total} selected',
  },

  buildingTool: {
    showAll: 'Show all',
    hideAll: 'Hide all',
    updateZone: 'Update zone',
    deleteZone: 'Delete zone',
    title: 'Building Tool',
    description:
      'Draw a polygon on the map to outline the building footprint, then adjust parameters and create a zone with walls.',
    numberOfFloors: 'Number of Floors',
    floorHeight: 'Floor Height (m)',
    startFloor: 'Start Floor',
    wallSegments: 'Wall Segments',
    totalPerimeter: 'Total Perimeter',
    creating: 'Creating...',
    createZoneAndWalls: 'Create Zone & Walls',
    createSuccess: 'Zone and {count} wall(s) created successfully',
    createError: 'Failed to create zone and walls',
    joinBearings: 'Join by bearing',
    splitBearings: 'Show all segments',
    joinButton: 'Join',
    splitButton: 'Split',
    floorArea: 'Floor Area (m²)',
    roofArea: 'Roof Area (m²)',
    autoSelect: 'Auto',
    autoSelectHint: 'Click a building to auto-select its footprint',
    noBuildingFound: 'No building found',
    createAnother: 'Create Another Zone',
    autoSelectTooltip: 'Auto-select',
    drawPolygonTooltip: 'Draw polygon',
    drawStackedTooltip: 'Add floor above zone',
    drawStackedTooltipDisabled:
      'Add floor above zone (need an existing zone first)',
    drawStackedHint: 'Click a zone to add a copy on top',
    snapToggleOn: 'Snap on',
    snapToggleOff: 'Snap off',
    splitTooltip: 'Split a zone',
    splitTooltipDisabled: 'Split a zone (need a zone first)',
    splitTargetHint: 'Click a zone to split',
    view3dTooltip: '3D view',
    splitPanelTitle: 'Split zone',
    splitPanelDescription:
      'Drag the sliders to position the cut. The zone splits into two halves.',
    splitPanelPickTargetHint: 'Click a zone on the map to pick what to split.',
    splitPanelSplittingZone: 'Splitting: {name}',
    splitPanelCutPositionLabel: 'Cut position: {value}',
    splitPanelCutAngleLabel: 'Cut angle: {value}°',
    splitPanelCutLengthLabel: 'Cut length: {value}',
    splitPanelCutOffsetLabel: 'Cut offset: {value}',
    splitPanelConfirm: 'Confirm split',
    splitPanelCommitting: 'Splitting…',
    errorUpdateZone: 'Failed to update zone',
    errorCreateStackedZone: 'Failed to create floor above zone',
    errorSplitZone: 'Failed to split zone',
    errorDeleteZone: 'Failed to delete zone',
  },

  underDevelopment: 'This page is under development.',

  // excel export labels for the input data sheet
  excelExport: {
    // column headers
    headerSection: 'Section',
    headerName: 'Name',
    headerParameter: 'Parameter',
    headerValue: 'Value',
    headerUnit: 'Unit',
    // sheet tab name
    inputDataSheetName: 'Input Data',
    // boolean values
    boolYes: 'Yes',
    boolNo: 'No',
    // section names
    sectionThermalZones: 'Thermal Zones',
    sectionExteriorWalls: 'Exterior Walls',
    sectionHvacUnits: 'HVAC Units',
    sectionHeatingCoolingModules: 'Heating/Cooling Modules',
    sectionCentralEnergyModules: 'Central Energy Modules',
    sectionHeatPumps: 'Heat Pumps',
    sectionDistrictHeating: 'District Heating',
    sectionCombustionBoilers: 'Combustion Boilers',
    sectionElectricBoilers: 'Electric Boilers',
    sectionSolarThermal: 'Solar Thermal',
    sectionOtherEnergySources: 'Other Energy Sources',
    sectionSolarPv: 'Solar PV',
    sectionDomesticHotWater: 'Domestic Hot Water',
    sectionPipeConfigurations: 'Pipe Configurations',
    sectionProductionEquipment: 'Production Equipment',
    sectionExcessEnergy: 'Excess Energy',
    // concept matches common.* but formatting differs for sheet readability
    area: 'Area',
    uValue: 'U-value',
    yearInstalled: 'Year installed',
    energySource: 'Energy source',
    systemEfficiency: 'System efficiency',
    usedForHeating: 'Used for heating',
    usedForCooling: 'Used for cooling',
    usedForDHW: 'Used for DHW',
    maxCapacity: 'Max capacity',
    heatingCapacity: 'Heating capacity',
    cop: 'COP',
    heatingEffect: 'Heating effect',
    coolingEffect: 'Cooling effect',
    co2eEmissionFactor: 'CO\u2082e emission factor',
    energyPrice: 'Energy price',
    power: 'Power',
    // entity prefixes
    prefixWall: 'Wall',
    prefixWindow: 'Window',
    prefixDoor: 'Door',
    prefixGate: 'Gate',
    prefixPipe: 'Pipe',
    prefixHeatingBattery: 'Heating battery',
    prefixCoolingBattery: 'Cooling battery',
    prefixDxSystem: 'DX system',
    prefixEnergySource: 'Energy source',
    prefixTank: 'Tank',
    prefixCopEntry: 'COP entry',
    // thermal zones
    heatedFloorArea: 'Heated floor area (BRA)',
    numberOfFloors: 'Number of floors',
    floorHeight: 'Floor height',
    indoorTemperature: 'Start temperature',
    zoneClimate: 'Zone climate',
    airLeakageGrade: 'Air leakage - Grade',
    airLeakageN50: 'Air leakage - n50',
    setpointHeating: 'Setpoint - Heating',
    setpointCooling: 'Setpoint - Cooling',
    thermalBridgeType: 'Thermal bridge - Type',
    thermalBridgePsiValue: 'Thermal bridge - \u03C8-value',
    floorType: 'Floor to ground - Type',
    floorArea: 'Floor to ground - Area',
    floorUValue: 'Floor to ground - U-value',
    floorHeatLossCoeff: 'Floor to ground - Heat loss coefficient',
    roofType: 'Roof - Type',
    roofArea: 'Roof - Area',
    roofUValue: 'Roof - U-value',
    roofAngle: 'Roof - Angle',
    roofWindowArea: 'Roof - Window area',
    roofWindowUValue: 'Roof - Window U-value',
    roofWindowGValue: 'Roof - Window g-value',
    basementArea: 'Basement - Floor area',
    basementWallPerimeter: 'Basement - Exposed perimeter',
    basementFillHeight: 'Basement - Depth below ground',
    basementWallThickness: 'Basement - Wall thickness',
    basementWallUValue: 'Basement - Wall U-value',
    basementFloorUValue: 'Basement - Floor U-value',
    groundThermalConductivity: 'Ground - Thermal conductivity',
    groundHeatCapacity: 'Ground - Heat capacity',
    groundPenetrationDepth: 'Ground - Penetration depth',
    thermalMassCategory: 'Thermal mass - Category',
    thermalMassHeatCapacity: 'Thermal mass - Heat capacity',
    thermalMassNormalized: 'Thermal mass - Normalized heat capacity',
    // windows
    gValue: 'g-value',
    shadingFactor: 'Shading factor',
    externalBlinds: 'External blinds',
    externalBlindsGt: 'External blinds Gt',
    externalScreen: 'External screen',
    externalScreenGt: 'External screen Gt',
    internalShading: 'Internal shading',
    internalShadingGt: 'Internal shading Gt',
    // hvac
    heatRecoveryType: 'Heat recovery type',
    motorType: 'Motor type',
    flowType: 'Flow type',
    sfpSupply: 'SFP (supply)',
    sfpExtract: 'SFP (extract)',
    sfpNonOperating: 'SFP non-operating',
    coverageArea: 'Coverage area',
    coverageVolume: 'Coverage volume',
    fanPowerSupply: 'Fan power (supply)',
    fanPowerExtract: 'Fan power (extract)',
    heatRecoveryEfficiency: 'Heat recovery efficiency',
    frostProtection: 'Frost protection',
    frostProtectionTemperature: 'Frost protection temperature',
    airVolumeOperatingSupply: 'Air volume operating (supply)',
    airVolumeCapacitySupply: 'Air volume capacity (supply)',
    airVolumeNonOperatingSupply: 'Air volume non-operating (supply)',
    airVolumeOperatingExtract: 'Air volume operating (extract)',
    airVolumeCapacityExtract: 'Air volume capacity (extract)',
    airVolumeNonOperatingExtract: 'Air volume non-operating (extract)',
    supplyTemperature: 'Supply temperature',
    onlyNaturalPassiveVentilation: 'Only natural/passive ventilation',
    batteryEer: 'EER',
    // heating/cooling modules
    systemType: 'System type',
    // central energy modules
    coolingOnly: 'Cooling only',
    ambientTemperature: 'Ambient temperature',
    tankPurpose: 'Purpose',
    tankVolume: 'Volume',
    tankInsulationGrade: 'Insulation grade',
    tankHeatLossCoeff: 'Heat loss coefficient',
    tankSetpointTemp: 'Setpoint temperature',
    tankMaxTemp: 'Max temperature',
    tankMinTemp: 'Min temperature',
    // heat pumps
    heatPumpType: 'Heat pump type',
    nominalLoad: 'Nominal load',
    minimumLoad: 'Minimum load',
    degradationCoefficient: 'Degradation coefficient',
    liquidSource: 'Liquid source',
    sourceTemperature: 'Source temperature',
    sourceYearlyAvgTemp: 'Source yearly avg temperature',
    sourceTempAmplitude: 'Source temperature amplitude',
    copSourceTemp: 'Source temp',
    copMaxEffectLow: 'Max effect (low temp)',
    copMaxEffectHigh: 'Max effect (high temp)',
    copFullLoadLow: 'COP full load (low temp)',
    copFullLoadHigh: 'COP full load (high temp)',
    copNormalLoadLow: 'COP normal load (low temp)',
    copNormalLoadHigh: 'COP normal load (high temp)',
    copLowLoadLow: 'COP low load (low temp)',
    copLowLoadHigh: 'COP low load (high temp)',
    // combustion boilers
    boilerType: 'Boiler type',
    efficiencyAtLowerLoad: 'Efficiency at lower load',
    effectAtFullLoad: 'Effect at full load',
    isCondensing: 'Is condensing',
    seasonalMeanTemperature: 'Seasonal mean temperature',
    fuelSource: 'Fuel source',
    fuelEnergyContent: 'Fuel energy content',
    fuelCo2EmissionFactor: 'Fuel CO\u2082 emission factor',
    operatingStart: 'Operating start',
    operatingEnd: 'Operating end',
    // electric boilers
    phi: 'Phi',
    insulationGrade: 'Insulation grade',
    // solar thermal
    tilt: 'Tilt',
    collectorArea: 'Collector area',
    pipeLength: 'Pipe length',
    pipeUValue: 'Pipe U-value',
    efficiencyAtNormalConditions: 'Efficiency at normal conditions',
    heatLossLinearCoefficient: 'Heat loss linear coefficient',
    heatLossCoefficient: 'Heat loss coefficient',
    // other energy sources
    fuelType: 'Fuel type',
    // solar pv
    panelArea: 'Panel area',
    nominalEfficiency: 'Nominal efficiency',
    panelAngle: 'Panel angle',
    panelAzimuth: 'Panel azimuth',
    inverterEfficiency: 'Inverter efficiency',
    degradationLoss: 'Degradation loss',
    inverterLoss: 'Inverter loss',
    transmissionProduct: 'Transmission product (0-1)',
    temperatureCoefficient: 'Temperature coefficient (%/°C)',
    incidentAngleModifier: 'Incident angle modifier (0-1)',
    heatLossFactor: 'Heat loss factor',
    pvMountingType: 'PV mounting type',
    windExposure: 'Wind exposure',
    pvCellType: 'PV cell type',
    // domestic hot water
    heaterType: 'Heater type',
    useNormedData: 'Use normed data',
    placementType: 'Placement type',
    volume: 'Volume',
    setpointTemperature: 'Setpoint temperature',
    heatingElementPower: 'Heating element power',
    specificHeatLoss: 'Specific heat loss',
    maxPowerDemand: 'Max power demand',
    usagePattern: 'Usage pattern',
    heatContributionPercent: 'Heat contribution percent',
    distributionSystemType: 'Distribution system type',
    pipeSpecificLength: 'Pipe specific length',
    tapCyclesPerDay: 'Tap cycles per day',
    pipesInHeatedRoom: 'Pipes in heated room',
    specificPumpPower: 'Specific pump power',
    pipeLinearUValue: 'Pipe linear U-value',
    avgTempOutsideHeatedRoom: 'Avg temp outside heated room',
    greyWaterRecovery: 'Grey water recovery',
    greyWaterEfficiency: 'Grey water efficiency',
    greyWaterShare: 'Grey water share',
    greyWaterConnectionLossFactor: 'Grey water connection loss factor',
    greyWaterPatternLossFactor: 'Grey water pattern loss factor',
    // pipe configurations
    waterSystemQuality: 'Water system quality',
    pipeInsulationType: 'Pipe insulation type',
    spp: 'SPP',
    constantFlow: 'Constant flow',
    lengthOfPipe: 'Length of pipe',
    percentageInHeatedSpace: 'Percentage in heated space',
    avgTempOutsideHeatedSpace: 'Avg temp outside heated space',
    temperatureReturn: 'Temperature return',
    temperatureSupply: 'Temperature supply',
    // production equipment
    eligibleForHarvesting: 'Eligible for harvesting',
    // excess energy
    medium: 'Medium',
    temperature: 'Temperature',
    amount: 'Amount',
    ductShape: 'Duct shape',
    diameter: 'Diameter',
    width: 'Width',
    height: 'Height',
    airSpeed: 'Air speed',
  },

  actions: {
    ventilation: {
      pageTitle: 'Ventilation',
      ventilationUnits: 'Ventilation Units',
      noHvacUnits:
        'No HVAC units found. Add HVAC units in the HVAC Systems section first.',
      usingUnits:
        'Using {count} enabled ventilation unit(s) from Building Inspection.',
      enableUnitsHint:
        'Enable ventilation units below to include them in the action.',
    },
    lighting: {
      pageTitle: 'Lighting',
      thermalZones: 'Thermal Zones',
      noZones:
        'No thermal zones found. Add thermal zones in the Thermal Zones section first.',
      usingZones:
        'Using {count} enabled thermal zone(s) for the lighting action.',
      enableZonesHint:
        'Enable thermal zones below to include them in the lighting action.',
      unit: 'W/m\u00B2',
      presenceControl: 'Presence control (lighting)',
      presenceControlLabel: 'Presence control:',
      presenceYes: 'Yes (factor: {value})',
      presenceNo: 'No',
    },
    heating: {
      pageTitle: 'Heating & Cooling',
      thermalZones: 'Thermal Zones',
      usingZones:
        'Using {count} enabled thermal zone(s) for the heating action.',
      enableZonesHint:
        'Enable thermal zones below to include them in the heating action.',
      addNewSystem: 'Add New System',
      addSystemDialogTitle: 'Add New System',
      template: 'Template',
      systemName: 'System Name',
      priorityHelp: '1 = highest priority',
      unnamed: 'Unnamed',
      chipNew: 'NEW',
      chipRemoved: 'REMOVED',
      chipModified: 'MODIFIED',
      remove: 'Remove',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this system?',
      undo: 'Undo',
      noExistingSystems: 'No existing heating/cooling systems in this zone.',
      enableZoneHint: 'Enable this zone to configure heating/cooling systems.',
      existingSystems: '{count} existing system(s)',
      templateAirWaterHP: 'Air/Water Heat Pump',
      templateAirAirHP: 'Air/Air Heat Pump',
      templateAirToAirHP: 'Air/Air Heat Pump',
      templateGroundSourceHP: 'Ground Source Heat Pump',
      templateElectricBoiler: 'Electric Boiler',
      templateDistrictHeating: 'District Heating',
      templateDistrictCooling: 'District Cooling',
    },
    solar: {
      pageTitle: 'Solar Cells',
      solarPvModules: 'Solar PV Modules',
      noModules:
        'No solar PV modules found. Add solar PV modules in the Solar PV section first.',
      usingModules:
        'Using {count} enabled solar PV module(s) from Building Inspection.',
      enableModulesHint:
        'Enable solar PV modules below to include them in the action.',
      addNewModule: 'Add New Module',
      chipNew: 'New',
      noExistingModules: 'No existing solar PV modules.',
    },
    control: {
      pageTitle: 'Control & Regulation',
      energyReductionPercent: 'Energy Reduction [%]',
      energyReductionHint:
        'Percentage reduction of total delivered energy from baseline simulation.',
    },
    overview: {
      overviewTitle: 'Actions Overview',
      combinedResults: 'Combined Simulation Results',
      notSimulated: 'Not yet simulated',
      noActiveActions:
        'No active actions. Enable actions in the individual action pages.',
      activeActions: 'Active Actions',
      runCombinedSimulation: 'Run Building Simulation',
      noUnitsSelected: 'No units selected',
      noZonesSelected: 'No thermal zones selected',
      noModulesSelected: 'No modules selected',
      totalInvestment: 'Total investment',
      priorityUpdateFailed: 'Failed to update priority',
    },
  },

  // scenario sync (out of sync banner + review dialog)
  scenarioSync: {
    outOfSync:
      'The inspection has changed since this scenario was last synced.',
    legacyOutOfSync:
      'The inspection has changed since this scenario was created. This scenario predates sync support, so create a new scenario to include the latest changes.',
    reviewChanges: 'Review changes',
    notSet: 'Not set',
    items: 'items',
    dialogTitle: 'Update scenario from inspection',
    dialogIntro:
      'Changes from the inspection are applied automatically and your scenario edits are kept. When both sides changed the same field, choose which value to keep.',
    conflictsHeading: 'conflicts need your review',
    noConflicts: '0 conflicts',
    added: 'New',
    removed: 'Removed',
    removedInScenario: 'Deleted in this scenario, the deletion is kept',
    removeConflict: 'Deleted in the inspection, but changed in this scenario',
    keepEntity: 'Keep it',
    removeEntity: 'Delete it',
    keepScenario: 'Keep scenario value',
    takeParent: 'Use inspection value',
    keptOverrides:
      'fields keep your scenario edits (the update does not overwrite them)',
    noChanges: 'No differences to sync.',
    markSynced: 'Mark as synced',
    applySync: 'Apply update',
    syncSuccess: 'Scenario updated from inspection',
    syncError: 'Failed to sync the scenario',
    resimulateHint: 'Re-run the simulation afterwards to refresh the results.',
    collections: {
      inspection: 'Inspection',
      thermalZones: 'Thermal Zones',
      exteriorWalls: 'Exterior Walls',
      windowAreas: 'Windows',
      doorAreas: 'Doors',
      gateAreas: 'Gates',
      operationalLoads: 'Internal Loads',
      thermalZoneLinks: 'Zone Links',
      hvacUnits: 'HVAC Systems',
      heatingCoolingModules: 'Heating & Cooling',
      centralEnergyModules: 'Central Energy',
      combustionBoilers: 'Combustion Boilers',
      districtHeatings: 'District Heating',
      electricBoilers: 'Electric Boilers',
      otherSources: 'Other Energy Sources',
      solarTermals: 'Solar Thermal',
      heatPumps: 'Heat Pumps',
      productionEquipments: 'Production Equipment',
      solarPvModules: 'Solar PV',
      excessEnergyModules: 'Excess Energy',
      domesticHotWaters: 'Domestic Hot Water',
      actionModules: 'Actions',
      pipeLinks: 'Pipe Links',
      batteries: 'Batteries',
    },
  },

  scenario: {
    intro:
      'Create one or more editable copies of this inspection. Change anything in a copy, then simulate to compare against the original.',
    introExisting:
      'A scenario clone of this inspection already exists. Open it to keep editing, or start fresh from the current state of the original.',
    bannerNotice:
      'Scenario. Changes here do not affect the original inspection.',
    createScenario: 'Create scenario',
    addScenario: 'Add scenario',
    scenarioName: 'Scenario name',
    scenarioNamePlaceholder: 'e.g. New ventilation system',
    unnamedScenario: 'Unnamed scenario',
    creating: 'Creating…',
    createNewScenario: 'Create new scenario',
    renameScenario: 'Edit action name and priority',
    deleteScenario: 'Delete scenario',
    confirmDeleteScenario: 'Remove this scenario?',
    working: 'Working…',
    backToOriginal: 'Back to original',
    confirmReplace:
      'Replace the existing scenario with a fresh clone of the original? The current scenario will be discarded.',
    createFailed: 'Failed to create scenario',
    replaceFailed: 'Failed to replace scenario',
    renameFailed: 'Failed to rename scenario',
    deleteFailed: 'Failed to delete scenario',
    existingCreated: 'Created {date}',
    existingCreatedBy: 'Created {date} by {name}',
    generalNotes: 'General notes',
    generalNotesPlaceholder:
      'Explain the action in as much detail as possible: what specifically changed and why.',
    notesRequired: 'General notes are required',
    investmentCost: 'Investment cost [NOK]',
    runToCompare: 'Run action simulation to compare',
    savingsUnavailable:
      'Savings could not be calculated yet. Price data is missing or incomplete.',
    compareLeftLabel: 'Original',
    compareRightLabel: 'Scenario',
    compareAgainst: 'Compare against',
    noComparison: 'No comparison',
    actionTypeLabel: 'Action type',
    selectActionType: 'Select action type',
    actionTypes: {
      buildingShell: 'Building Shell',
    },
  },

  report: {
    dragDropHint: 'Reorder chapters and fields in the sidebar.',
    generateReport: 'Generate report',
    reportLanguage: 'Report language',
    addChapter: 'Add chapter',
    exportToWord: 'Export to Word',
    exportingToWord: 'Exporting to Word...',
    exportingProgress: 'Exporting to Word — {current} of {total}: {label}',
    sources: 'Sources',
    noChapters: 'No chapters yet. Add a chapter to get started.',
    editTitle: 'Edit title',
    removeChapter: 'Remove chapter',
    noFields: 'No text fields yet. Add a field to get started.',
    fieldTitlePlaceholder: 'New field title...',
    addField: 'Add field',
    confirmRemoveChapter: 'Remove chapter "{title}"?',
    generateWithAI: 'Generate with AI',
    hideAuditLog: 'Hide audit log',
    showAuditLog: 'Show audit log',
    removeField: 'Remove field',
    promptLabel: 'Prompt (used for AI generation)',
    promptPlaceholder: 'Write instructions for AI generation...',
    generateText: 'Generate text',
    textPlaceholder: 'Write content here, or use AI generation',
    confirmRemoveField: 'Remove field "{title}"?',
    addChapterTitle: 'Add new chapter',
    chapterTitleLabel: 'Chapter title',
    chapterTitlePlaceholder: 'E.g. Indoor climate and comfort',
    fetchError: 'Could not fetch report',
    saveError: 'Could not save report',
    generateError: 'Could not generate text',
    generatingStatus:
      'Report is being generated by AI. This may take several minutes — the page will update automatically when ready.',
    generateSuccess: 'Report generated successfully',
    generateTimeout:
      'Report generation timed out. The service may still be processing — try refreshing the page later.',
    noContent:
      'No content yet. Generate the report or add text to see a preview.',
    generationPromptLog: 'Generated with prompt:',
  },
};

export default BUILDING_INSPECTION;
